/* global APP, MeetHourJS, config, interfaceConfig */

import { BrowserDetection } from '@jitsi/js-utils';
import EventEmitter from 'events';
import i18next from 'i18next';
import { sha256 } from 'js-sha256';
import Logger from 'meet-hour-logger';
import moment from 'moment';

import {
    CHROME,
    FIREFOX,
    INTERNET_EXPLORER,
    OPERA,
    SAFARI
} from './browser';
import { openConnection } from './connection';
import { ENDPOINT_TEXT_MESSAGE_NAME } from './modules/API/constants';
import { AUDIO_ONLY_SCREEN_SHARE_NO_TRACK } from './modules/UI/UIErrors';
import AuthHandler from './modules/UI/authentication/AuthHandler';
import UIUtil from './modules/UI/util/UIUtil';
import mediaDeviceHelper from './modules/devices/mediaDeviceHelper';
import Recorder from './modules/recorder/Recorder';
import { createTaskQueue } from './modules/util/helpers';
import {
    createDeviceChangedEvent,
    createScreenSharingEvent,
    createStartSilentEvent,
    createTrackMutedEvent,
    sendAnalytics
} from './react/features/analytics';
import {
    maybeRedirectToWelcomePage,
    redirectToStaticPage,
    reloadNow,
    reloadWithStoredParams
} from './react/features/app/actions';
import {
    AVATAR_URL_COMMAND,
    DESIGNATION_COMMAND,
    EMAIL_COMMAND,
    ORGANIZATION_COMMAND,
    authStatusChanged,
    commonUserJoinedHandling,
    commonUserLeftHandling,
    conferenceFailed,
    conferenceJoinInProgress,
    conferenceJoined,
    conferenceLeft,
    conferenceSubjectChanged,
    conferenceTimestampChanged,
    conferenceUniqueIdSet,
    conferenceWillInit,
    conferenceWillJoin,
    conferenceWillLeave,
    dataChannelOpened,
    endpointMessageReceived,
    kickedOut,
    lockStateChanged,
    nonParticipantMessageReceived,
    onStartMutedPolicyChanged,
    p2pStatusChanged,
    sendLocalParticipant,
    transportStateChanged,
    updateConferenceCallEnded
} from './react/features/base/conference';
import { getConferenceOptions } from './react/features/base/conference/functions';
import { overwriteConfig } from './react/features/base/config/actions';
import { apiKEY } from './react/features/base/config/constants';
import getRoomName from './react/features/base/config/getRoomName';
import {
    checkAndNotifyForNewDevice,
    getAvailableDevices,
    getDefaultDeviceId,
    notifyCameraError,
    notifyMicError,
    setAudioOutputDeviceId,
    updateDeviceList
} from './react/features/base/devices';
import {
    MHConferenceErrors,
    MHConferenceEvents,
    MHConnectionErrors,
    MHConnectionEvents,
    MHMediaDevicesEvents,
    MHParticipantConnectionStatus,
    MHTrackErrors,
    MHTrackEvents,
    browser,
    isFatalMHConnectionError
} from './react/features/base/lib-meet-hour';
import {
    MEDIA_TYPE,
    getStartWithAudioMuted,
    getStartWithVideoMuted,
    isVideoMutedByUser,
    setAudioAvailable,
    setAudioMuted,
    setScreenshareMuted,
    setVideoAvailable,
    setVideoMuted
} from './react/features/base/media';
import { fetchAxiosApi } from './react/features/base/mtApi';
import {
    dominantSpeakerChanged,
    getLocalParticipant,
    getNormalizedDisplayName,
    getParticipantById,
    isLocalParticipantForceMutedAudio,
    isLocalParticipantForceMutedVideo,
    localParticipantConnectionStatusChanged,
    localParticipantRoleChanged,
    participantConnectionStatusChanged,
    participantDesignation,
    participantKicked,
    participantMutedUs,
    participantOrganization,
    participantPresenceChanged,
    participantRoleChanged,
    participantUpdated,
    updateRemoteParticipantFeatures
} from './react/features/base/participants';
import {
    getUserSelectedCameraDeviceId,
    updateSettings
} from './react/features/base/settings';
import {
    addLocalTrack,
    createLocalPresenterTrack,
    createLocalTracksF,
    destroyLocalTracks,
    getLocalDesktopTrack,
    getLocalMHAudioTrack,
    getLocalMHVideoTrack,
    getLocalTracks,
    getLocalVideoTrack,
    getLocalVideoTrackDesktop,
    isLocalCameraTrackMuted,
    isLocalTrackMuted,
    isUserInteractionRequiredForUnmute,
    replaceLocalTrack,
    trackAdded,
    trackMutedChanged,
    trackRemoved
} from './react/features/base/tracks';
import { isDomainWeborNative } from './react/features/base/util/checkOS';
import {
    RUNTIME_PREFER_BOSH_LOCK_KEY,
    RUNTIME_PREFER_BOSH_SESSION_KEY,
    RUNTIME_PREFER_BOSH_SET_AT_KEY,
    RUNTIME_WS_FAILURE_COUNT_KEY,
    appendRoomParam,
    forceRuntimePreferBosh,
    recordMediaRelayRequired,
    setMediaRelayPolicyApplied,
    wasMediaRelayPolicyApplied
} from './react/features/base/util/connectionUtils';
import { downloadJSON } from './react/features/base/util/downloadJSON';
import { showDesktopPicker } from './react/features/desktop-picker';
import { appendSuffix, openDisplayNamePrompt } from './react/features/display-name';
import {
    maybeOpenFeedbackDialog,
    submitFeedback
} from './react/features/feedback';
import { selectParticipantInLargeVideo } from './react/features/large-video/actions.any';
import { setNoiseSuppressionEnabled } from './react/features/noise-suppression/actions';
import { getNoiseSuppressionEnabledOnStartup, isNoiseSuppressionEnabled } from './react/features/noise-suppression/functions';
import {
    NOTIFICATION_TIMEOUT_TYPE,
    hideNotification,
    showErrorNotification,
    showNotification,
    showWarningNotification
} from './react/features/notifications';
import {
    mediaPermissionPromptVisibilityChanged,
    toggleSlowGUMOverlay
} from './react/features/overlay';
import { suspendDetected } from './react/features/power-monitor';
import {
    initPrejoin,
    isDisplayNameRequired,
    isPrejoinPageEnabled,
    isPrejoinPageVisible,
    makePrecallTest,
    setPrejoinJoinError,
    setPrejoinPageVisibility
} from './react/features/prejoin';
import { stopLocalVideoRecording } from './react/features/recording/actions.any';
import LocalRecordingManager from './react/features/recording/components/Recording/LocalRecordingManager.web';
import { disableReceiver, stopReceiver } from './react/features/remote-control';
import { isScreenAudioShared } from './react/features/screen-share/functions';
import { toggleScreenshotCaptureEffect } from './react/features/screenshot-capture';
import { AudioMixerEffect } from './react/features/stream-effects/audio-mixer/AudioMixerEffect';
import { createPresenterEffect } from './react/features/stream-effects/presenter';
import { createRnnoiseProcessor } from './react/features/stream-effects/rnnoise';
import {
    sendWhiteBoardInstance,
    toogleEtherPad
} from './react/features/toolbox/actions.web';
import { transcriberJoined, transcriberLeft } from './react/features/transcribing/actions';
import { toggleTileView } from './react/features/video-layout';
import UIEvents from './service/UI/UIEvents';

// import { showModeratedNotification } from './react/features/av-moderation/actions';
// import { shouldShowModeratedNotification } from './react/features/av-moderation/functions';

const logger = Logger.getLogger(__filename);

const DEFAULT_RUNTIME_WS_RECOVERY_PROBE_INTERVAL_MS = 30 * 1000;
const DEFAULT_RUNTIME_WS_RECOVERY_PROBE_TIMEOUT_MS = 4 * 1000;
const DEFAULT_PREJOIN_JOIN_TIMEOUT_MS = 30 * 1000;
const DEFAULT_RUNTIME_ALLOW_WEBSOCKET_RETURN = false;
const CONNECTION_TIMEOUT_ERROR = 'CONNFAIL_TIMEOUT';
const PREJOIN_JOIN_TIMEOUT_ERROR = 'PREJOIN_JOIN_TIMEOUT';
const MAX_AUTO_RECONNECT_DURATION_MS = 2 * 60 * 1000;
const AUTO_RECONNECT_COUNTDOWN_SECONDS = 10;
const AUTO_RECONNECT_RELOAD_HASH_OVERRIDES = {
    'config.prejoinPageEnabled': false,
    'interfaceConfig.ENABLE_DESKTOP_DEEPLINK': false,
    'interfaceConfig.MOBILE_APP_PROMO': false
};
const DEBUG_FLAG_FORCE_UDP_BLOCK = 'forceUdpBlockFailure';
const DEBUG_FLAG_FORCE_BRIDGE_CHANNEL_CLOSED = 'forceBridgeChannelClosed';
const DEBUG_FLAG_FORCE_WEBSOCKET_CLOSED = 'forceWebSocketClosed';

const eventEmitter = new EventEmitter();

let room;
let connection;
let _runtimeTransportReconnectInProgress = false;
let _runtimeWsRecoveryProbeInFlight = false;
let _runtimeWsRecoveryProbeTimer;
let _autoReconnectStartedAt = 0;
let _autoReconnectWindowTimer;
let _autoReconnectCountdownTimer;
let _autoReconnectNotificationUid;

/**
 * Returns true if the URL query parameter is enabled with a truthy value.
 *
 * @param {string} key - Query parameter key.
 * @returns {boolean}
 */
function _isDebugFlagEnabled(key) {
    try {
        const value = new URLSearchParams(window.location.search).get(key);

        return value === '1' || value === 'true' || value === 'yes';
    } catch (error) {
        return false;
    }
}

/**
 * Consumes a debug flag once per session so forced failures do not loop.
 *
 * @param {string} key - Query parameter key.
 * @returns {boolean}
 */
function _consumeDebugFlagOncePerSession(key) {
    if (!_isDebugFlagEnabled(key)) {
        return false;
    }

    const consumedKey = `mh_debug_flag_consumed_${key}`;

    try {
        if (window.sessionStorage?.getItem(consumedKey) === '1') {
            return false;
        }

        window.sessionStorage?.setItem(consumedKey, '1');
    } catch (error) {
        // Ignore storage errors and continue with best effort behavior.
    }

    return true;
}

/**
 * Handles a closed bridge (JVB) data channel.
 *
 * This deliberately does NOT tear the conference down. A bridge channel close says nothing about the
 * XMPP transport and, in a 1-1 call, nothing about media either:
 *
 * - While P2P is active, media does not traverse the bridge at all. The JVB peer connection is only
 *   kept warm with its media transfer suspended, so its data channel closing is expected and harmless.
 * - When the second participant leaves a 1-1 call, the bridge closes the channel on purpose because the
 *   remaining endpoint is alone.
 * - Jicofo moving the conference to another bridge closes the old channel by design.
 *
 * {@link BridgeChannel._retryConnection} already re-creates the channel with exponential backoff, and
 * {@link IceFailedHandling} already covers the case where JVB media is genuinely broken. Reacting here
 * by pinning the session to BOSH, forcing TURN relay and reconnecting the whole conference in-page
 * turned every one of those benign closes into a visible "Reconnecting..." for the participant - and,
 * because the reconnect drops the XMPP connection mid-handshake, into `"session-initiate" error null`
 * and `session-terminate ... reason: timeout` for whoever was negotiating P2P with them.
 *
 * @param {string} reason - Human readable reason.
 * @param {*} details - Optional bridge close details.
 * @param {Object} [options] - Extra options.
 * @param {boolean} [options.escalate=false] - Force the old failover behaviour. Only the injected debug
 * scenario sets this; a real close must never escalate.
 * @returns {void}
 */
function _handleBridgeDataChannelClosed(reason, details, { escalate = false } = {}) {
    if (!escalate) {
        logger.warn('Bridge data channel closed; letting the bridge channel retry on its own.', details);

        // Telemetry only. `userVisibleType` is deliberately omitted: the transport middleware treats it
        // as the trigger for the BOSH/relay recovery profile, and there is nothing to recover from here.
        APP.store.dispatch(transportStateChanged({
            diagnosticsSupported: true,
            mediaStatus: 'unchanged',
            reason,
            signalingPath: config?.preferBosh ? 'bosh' : 'websocket',
            signalingStatus: 'healthy',
            source: 'bridge-data-channel-closed'
        }));

        return;
    }

    logger.warn('Bridge data channel closed; forcing BOSH failover.', details);

    if (_forceBoshTransportPreference(reason)) {
        _forceRelayMediaFallback(reason);
        _reconnectConferenceInPage(reason);
    }
}

/**
 * Applies failover profile and reconnect after a simulated UDP blocked scenario.
 *
 * @param {string} reason - Human readable reason.
 * @returns {void}
 */
function _handleUdpBlockedScenario(reason) {
    logger.warn('UDP blocked scenario detected; forcing relay+BOSH fallback.');

    if (_forceBoshTransportPreference(reason)) {
        _forceRelayMediaFallback(reason);
        _reconnectConferenceInPage(reason);
    }
}

/**
 * Applies transport fallback and reconnect after a simulated websocket close.
 *
 * @param {string} reason - Human readable reason.
 * @returns {void}
 */
function _handleWebSocketClosedScenario(reason) {
    logger.warn('WebSocket closed scenario detected; forcing BOSH reconnect.');

    if (_forceBoshTransportPreference(reason)) {
        APP.store.dispatch(transportStateChanged({
            diagnosticsSupported: true,
            mediaStatus: 'unchanged',
            reason,
            signalingPath: 'bosh',
            signalingStatus: 'fallback-active',
            source: 'websocket-closed',
            userVisibleSeverity: 'warn',
            userVisibleType: 'websocket-fallback',
            websocketStatus: 'failed'
        }));
        _reconnectConferenceInPage(reason);
    }
}

/**
 * Clears runtime BOSH preference state so future connects can use WebSocket again.
 *
 * @returns {void}
 */
function _clearRuntimePreferBoshState() {
    try {
        window.sessionStorage?.removeItem(RUNTIME_PREFER_BOSH_SESSION_KEY);
        window.sessionStorage?.removeItem(RUNTIME_PREFER_BOSH_SET_AT_KEY);
        window.sessionStorage?.removeItem(RUNTIME_PREFER_BOSH_LOCK_KEY);
        window.sessionStorage?.removeItem(RUNTIME_WS_FAILURE_COUNT_KEY);
    } catch (error) {
        logger.warn('Failed clearing runtime BOSH preference state.', error);
    }

    if (config?.preferBosh) {
        config.preferBosh = false;
        APP.store.dispatch(overwriteConfig({ preferBosh: false }));
    }
}

/**
 * Forces runtime BOSH preference and config-level preference for subsequent reconnects.
 *
 * @param {string} reason - Human readable failover reason.
 * @returns {boolean}
 */
function _forceBoshTransportPreference(reason) {
    if (!config?.bosh) {
        return false;
    }

    forceRuntimePreferBosh(config, logger, { reason });

    if (!config?.preferBosh) {
        config.preferBosh = true;
        APP.store.dispatch(overwriteConfig({ preferBosh: true }));
    }

    return true;
}

/**
 * Forces a media fallback profile suitable for UDP-restricted networks.
 *
 * @param {string} reason - Human readable failover reason.
 * @returns {boolean}
 */
function _forceRelayMediaFallback(reason) {
    try {
        const currentP2p = config?.p2p || {};

        // Force the relay, but leave P2P enabled.
        //
        // "This network needs a TURN relay" and "this network cannot do P2P" are different statements, and
        // conflating them made every 1-1 call on a restricted network worse rather than better. A P2P
        // session built with iceTransportPolicy=relay reaches the peer through the same TURN server the
        // JVB path would have to use anyway, with one hop fewer and without consuming bridge capacity. The
        // relay covers the actual failure; disabling P2P only removed the better of the two remaining
        // paths, and because the flag is persisted for runtimeForceRelayTtlMs it kept doing so for every
        // conference joined in that tab for the next half hour.
        const nextP2p = {
            ...currentP2p,
            iceTransportPolicy: 'relay'
        };

        // Persist across the page reload performed by _reconnectConferenceInPage(). Without this the
        // retry is built with the same all-candidates policy that just failed, and the participant loops.
        recordMediaRelayRequired(logger, reason);

        config.forceTurnRelay = true;

        // Keep TURN/UDP available. On a network that blocks direct peer traffic but allows UDP egress,
        // TURN/UDP is the lowest-latency relay; removing it forces those users onto TURN/TCP or TLS and
        // adds head-of-line blocking to their audio for no benefit. ICE still prefers UDP over TCP by
        // candidate priority, so TCP/TLS is used only when UDP genuinely cannot get through.
        config.useTurnUdp = config.useTurnUdp !== false;
        config.p2p = nextP2p;

        APP.store.dispatch(overwriteConfig({
            forceTurnRelay: true,
            useTurnUdp: config.useTurnUdp,
            p2p: nextP2p
        }));

        APP.store.dispatch(transportStateChanged({
            diagnosticsSupported: true,
            forceTurnRelay: true,
            mediaStatus: 'relay-forced',
            reason,
            relayProtocol: 'tcp-or-tls-preferred',
            signalingPath: config?.preferBosh ? 'bosh' : 'websocket',
            signalingStatus: config?.preferBosh ? 'fallback-active' : 'healthy',
            source: 'relay-media-fallback',
            userVisibleSeverity: 'warn',
            userVisibleType: 'restricted-network',
            websocketStatus: config?.preferBosh ? 'degraded' : 'healthy'
        }));

        logger.warn(`Applied relay media fallback profile (${reason}): forceTurnRelay=true, useTurnUdp=false, p2p.disabled.`);

        return true;
    } catch (error) {
        logger.warn('Failed to apply relay media fallback profile.', error);

        return false;
    }
}

/**
 * Stops the periodic websocket recovery probe.
 *
 * @returns {void}
 */
function _stopRuntimeWebSocketRecoveryProbe() {
    if (_runtimeWsRecoveryProbeTimer) {
        window.clearInterval(_runtimeWsRecoveryProbeTimer);
        _runtimeWsRecoveryProbeTimer = undefined;
    }

    _runtimeWsRecoveryProbeInFlight = false;
}

/**
 * Reconnects the conference in-page using the currently preferred transport.
 *
 * @param {string} reason - Human readable reason for analytics/logging.
 * @returns {void}
 */
function _reconnectConferenceInPage(reason) {
    if (_runtimeTransportReconnectInProgress) {
        return;
    }

    const roomName = APP.conference?.roomName;

    if (!roomName) {
        return;
    }

    _runtimeTransportReconnectInProgress = true;

    logger.warn(`Reconnecting conference in-page (${reason}) with preferred transport.`);

    Promise.resolve()
        .then(async () => {
            APP.store.dispatch(setPrejoinJoinError(''));

            if (room) {
                APP.store.dispatch(conferenceWillLeave(room));
                APP.conference._rememberLocalTracksBeforeLeave();
                await room.leave().catch(error => {
                    logger.warn('Room leave failed during runtime transport reconnect.', error);
                });
            }

            await disconnect();

            // Tear local media down the way hangup() does before building the new conference. init() creates
            // fresh camera/mic tracks, and room.leave() dropped the old ones from the store without disposing them,
            // so they kept capturing - and an exclusive camera then fails to reopen.
            await APP.conference._resetLocalMediaForRejoin();

            return APP.conference.init({ roomName });
        })
        .catch(error => {
            logger.error('Runtime transport reconnect failed.', error);
        })
        .finally(() => {
            _runtimeTransportReconnectInProgress = false;
        });
}

/**
 * Probes the configured websocket endpoint and upgrades away from BOSH if stable.
 *
 * @returns {void}
 */
function _startRuntimeWebSocketRecoveryProbe() {
    if (_runtimeWsRecoveryProbeTimer) {
        return;
    }

    const configuredAllowReturn = config?.runtimeAllowWebSocketReturn;
    const allowWebSocketReturn = typeof configuredAllowReturn === 'boolean'
        ? configuredAllowReturn
        : DEFAULT_RUNTIME_ALLOW_WEBSOCKET_RETURN;

    // The probe exists for one purpose: to decide when a session can move back off BOSH. With the return
    // disabled there is no decision to make, so scheduling it would open a throwaway WebSocket to the XMPP
    // endpoint every interval for the rest of the meeting and throw the answer away - one connection
    // attempt per degraded client per interval, against prosody, for no outcome. This check used to live
    // inside finalize(), which runs after the socket has already been opened.
    if (!allowWebSocketReturn) {
        logger.warn('WebSocket recovery probe not started: runtimeAllowWebSocketReturn is disabled, so a '
            + 'successful probe would have nothing to act on.');

        return;
    }

    const configuredInterval = Number(config?.runtimeWebSocketRecoveryProbeIntervalMs);
    const intervalMs = Number.isFinite(configuredInterval) && configuredInterval > 0
        ? configuredInterval
        : DEFAULT_RUNTIME_WS_RECOVERY_PROBE_INTERVAL_MS;

    _runtimeWsRecoveryProbeTimer = window.setInterval(() => {
        if (_runtimeWsRecoveryProbeInFlight) {
            return;
        }

        if (!config?.websocket || !config?.bosh) {
            return;
        }

        const runtimePreferBosh = window.sessionStorage?.getItem(RUNTIME_PREFER_BOSH_SESSION_KEY) === '1';

        if (!runtimePreferBosh && !config?.preferBosh) {
            return;
        }

        _runtimeWsRecoveryProbeInFlight = true;

        const configuredTimeout = Number(config?.runtimeWebSocketRecoveryProbeTimeoutMs);
        const timeoutMs = Number.isFinite(configuredTimeout) && configuredTimeout > 0
            ? configuredTimeout
            : DEFAULT_RUNTIME_WS_RECOVERY_PROBE_TIMEOUT_MS;
        const wsUrl = appendRoomParam(config.websocket, APP.conference?.roomName || getRoomName());
        let probeSocket;
        const probeState = {
            timeoutId: null
        };
        let done = false;

        const finalize = healthy => {
            if (done) {
                return;
            }

            done = true;
            clearTimeout(probeState.timeoutId);

            try {
                probeSocket?.close();
            } catch (error) {
                // ignore
            }

            // No allowWebSocketReturn check here - the probe is not scheduled at all when the return is
            // disabled, so reaching this point means acting on the result is what was asked for.
            if (healthy) {
                logger.info('WebSocket recovery probe succeeded; re-enabling websocket preference.');
                _clearRuntimePreferBoshState();

                const isUsingWebSocket = Boolean(APP.connection?.xmpp?.connection?.isUsingWebSocket);

                if (APP.connection && room && !isUsingWebSocket) {
                    _reconnectConferenceInPage('websocket-recovered');
                }
            }

            _runtimeWsRecoveryProbeInFlight = false;
        };

        probeState.timeoutId = setTimeout(() => finalize(false), timeoutMs);

        try {
            probeSocket = new WebSocket(wsUrl);
        } catch (error) {
            logger.warn('WebSocket recovery probe initialization failed.', error);
            finalize(false);

            return;
        }

        probeSocket.onopen = () => finalize(true);
        probeSocket.onerror = () => finalize(false);
    }, intervalMs);
}

/**
 * The promise is used when the prejoin screen is shown.
 * While the user configures the devices the connection can be made.
 *
 * @type {Promise<Object>}
 * @private
 */
let _connectionPromise;

/**
 * This promise is used for chaining mutePresenterVideo calls in order to avoid  calling GUM multiple times if it takes
 * a while to finish.
 *
 * @type {Promise<void>}
 * @private
 */
let _prevMutePresenterVideo = Promise.resolve();

/*
 * Logic to open a desktop picker put on the window global for
 * lib-meet-hour to detect and invoke
 */
window.MeetHourScreenObtainer = {
    openDesktopPicker(options, onSourceChoose) {
        APP.store.dispatch(showDesktopPicker(options, onSourceChoose));
    }
};

/**
 * Maps the names of the browsers from bowser to the internal names defined in
 * ./browsers.js.
 */
const bowserNameToJitsiName = {
    'Chrome': CHROME,
    'Chromium': CHROME,
    'Opera': OPERA,
    'Firefox': FIREFOX,
    'Internet Explorer': INTERNET_EXPLORER,
    'Safari': SAFARI
};

/**
 * Known custom conference commands.
 */
const commands = {
    AVATAR_URL: AVATAR_URL_COMMAND,
    CONFERENCE_SUBJECT: 'CONFERENCE_SUBJECT',
    CUSTOM_ROLE: 'custom-role',
    EMAIL: EMAIL_COMMAND,
    ETHERPAD: 'etherpad',
    SHARED_VIDEO: 'shared-video',
    DESIGNATION: 'designation',
    ORGANIZATION: 'organization',
    WHITEBOARD: 'genericiframe'
};

/**
 * Open Connection. When authentication failed it shows auth dialog.
 *
 * @param roomName - The room name to use.
 * @returns Promise<MHConnection>.
 */
function connect(roomName) {
    APP.store.dispatch(setPrejoinJoinError(''));

    const configuredTimeout = Number(config?.prejoinJoinTimeoutMs);
    const timeoutMs = Number.isFinite(configuredTimeout) && configuredTimeout > 0
        ? configuredTimeout
        : DEFAULT_PREJOIN_JOIN_TIMEOUT_MS;
    const timeoutPromise = new Promise((resolve, reject) => {
        setTimeout(() => reject(new Error(CONNECTION_TIMEOUT_ERROR)), timeoutMs);
    });

    return Promise.race([
        openConnection({
            retry: true,
            roomName
        }),
        timeoutPromise
    ]).catch(err => {
        const isConnectionTimeout = err === CONNECTION_TIMEOUT_ERROR
            || err?.message === CONNECTION_TIMEOUT_ERROR;

        if (err === MHConnectionErrors.PASSWORD_REQUIRED) {
            // APP.UI.notifyTokenAuthFailed();
        } else {
            const connectionFailedText = i18next.t('connection.CONNFAIL');

            APP.store.dispatch(setPrejoinJoinError(connectionFailedText));

            if (isPrejoinPageVisible(APP.store.getState())) {
                APP.store.dispatch(setPrejoinPageVisibility(true));
            }

            APP.UI.notifyConnectionFailed(isConnectionTimeout ? undefined : err);
        }
        throw err;
    });
}

/**
 * Share data to other users.
 *
 * @param command - The command.
 * @param {string} value - New value.
 */
function sendData(command, value) {
    if (!room) {
        return;
    }

    room.removeCommand(command);
    room.sendCommand(command, { value });
}

/**
 * Get user nickname by user id.
 *
 * @param {string} id - User id.
 * @returns {string?} User nickname or undefined if user is unknown.
 */
function getDisplayName(id) {
    const participant = getParticipantById(APP.store.getState(), id);

    return participant && participant.name;
}

/**
 * Mute or unmute local audio stream if it exists.
 *
 * @param {boolean} muted - If audio stream should be muted or unmuted.
 */
function muteLocalAudio(muted) {
    APP.store.dispatch(setAudioMuted(muted));
}

/**
 * Mute or unmute local video stream if it exists.
 *
 * @param {boolean} muted - If video stream should be muted or unmuted.
 *
 */
function muteLocalVideo(muted) {
    APP.store.dispatch(setVideoMuted(muted));
}

/**
 * A queue for the async replaceLocalTrack action so that multiple audio
 * replacements cannot happen simultaneously. This solves the issue where
 * replaceLocalTrack is called multiple times with an oldTrack of null, causing
 * multiple local tracks of the same type to be used.
 *
 * @private
 * @type {Object}
 */
const _replaceLocalAudioTrackQueue = createTaskQueue();

/**
 * A task queue for replacement local video tracks. This separate queue exists
 * so video replacement is not blocked by audio replacement tasks in the queue
 * {@link _replaceLocalAudioTrackQueue}.
 *
 * @private
 * @type {Object}
 */
const _replaceLocalVideoTrackQueue = createTaskQueue();

/**
 *
 */
class ConferenceConnector {
    /**
     *
     */
    constructor(resolve, reject) {
        this._resolve = resolve;
        this._reject = reject;
        this.reconnectTimeout = null;
        room.on(
            MHConferenceEvents.CONFERENCE_JOINED,
            this._handleConferenceJoined.bind(this)
        );
        room.on(
            MHConferenceEvents.CONFERENCE_FAILED,
            this._onConferenceFailed.bind(this)
        );
    }

    /**
     *
     */
    _handleConferenceFailed(err) {
        this._unsubscribe();
        this._reject(err);
    }

    /**
     *
     */
    _onConferenceFailed(err, ...params) {
        APP.store.dispatch(conferenceFailed(room, err, ...params));

        if (err === MHConferenceErrors.PASSWORD_REQUIRED
                || err === MHConferenceErrors.MEMBERS_ONLY_ERROR
                || err === MHConferenceErrors.AUTHENTICATION_REQUIRED
                || err === MHConferenceErrors.DISPLAY_NAME_REQUIRED) {
            logger.warn('CONFERENCE FAILED (expected flow):', err, ...params);
        } else {
            logger.error('CONFERENCE FAILED:', err, ...params);
        }

        switch (err) {
        case MHConferenceErrors.NOT_ALLOWED_ERROR: {
            // let's show some auth not allowed page
            APP.store.dispatch(
                    redirectToStaticPage('static/authError.html')
            );
            break;
        }

        // not enough rights to create conference
        case MHConferenceErrors.AUTHENTICATION_REQUIRED: {
            // Waiting/retry is already owned by the authentication middleware.
            // Scheduling it here as well creates a second join loop and floods
            // the conference with repeated authentication failures.

            break;
        }

        case MHConferenceErrors.RESERVATION_ERROR: {
            const [ code, msg ] = params;

            APP.UI.notifyReservationError(code, msg);
            break;
        }

        case MHConferenceErrors.GRACEFUL_SHUTDOWN:
            APP.UI.notifyGracefulShutdown();
            break;

            // FIXME FOCUS_DISCONNECTED is a confusing event name.
            // What really happens there is that the library is not ready yet,
            // because Jicofo is not available, but it is going to give it another
            // try.
        case MHConferenceErrors.FOCUS_DISCONNECTED: {
            const [ focus, retrySec ] = params;

            APP.UI.notifyFocusDisconnected(focus, retrySec);
            break;
        }

        case MHConferenceErrors.VIDEOBRIDGE_NOT_AVAILABLE:
            // Jicofo has no bridge to allocate. This is OUR capacity problem, not the participant's
            // network, so it must not share the ICE_FAILED path: forcing TURN relay cannot help when
            // there is no bridge to relay to, and it would pin this user to relay-only ICE for the next
            // 30 minutes even though their network was never at fault. Telling them their firewall is
            // blocking media would be plainly wrong.
            //
            // Retry instead - bridge capacity is usually transient, and it becomes more common under the
            // load levels this deployment is scaling towards.
            logger.warn('No videobridge available; retrying without changing transport preferences.');
            APP.store.dispatch(showWarningNotification({
                descriptionKey: 'notify.videobridgeUnavailableDescription',
                titleKey: 'notify.videobridgeUnavailableTitle'
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));

            if (config?.bosh) {
                // Reconnect after a randomised delay, never immediately.
                //
                // Jicofo answers `bridgeNotAvailable` to every client in every room at once - the usual
                // cause is that the signalling server restarted and the bridges have not finished
                // re-registering yet. Reconnecting with no delay meant the whole deployment came back in
                // lockstep, hit the same not-yet-ready Jicofo, and bounced again, so one server blip
                // turned into several. The jitter spreads the herd out and gives the bridges a few
                // seconds to re-register before the retries land.
                const retryDelay = 2000 + Math.floor(Math.random() * 8000);

                logger.warn(`No videobridge available; reconnecting in ${retryDelay}ms.`);
                window.setTimeout(
                    () => _reconnectConferenceInPage(`conference-failure-${err}`),
                    retryDelay);

                break;
            }

            APP.store.dispatch(conferenceWillLeave(room));
            room.leave().then(() => connection.disconnect());

            break;

        case MHConferenceErrors.ICE_FAILED: {
            // ICE failure is a MEDIA-plane problem. It says nothing about whether the signaling
            // websocket is healthy, so this no longer touches the signaling transport - forcing BOSH here
            // only degraded signaling for users whose signaling was fine.
            //
            // The question here is strictly "was a relay-only path already tried and did it fail too",
            // so it has to be answered by the policy this page load actually built its peer connections
            // with. The persisted "this network needs relay" flag cannot answer it: it is also written by
            // transport events that never rebuild a peer connection (a closed bridge data channel, say),
            // and it survives 30 minutes across other joins in the same tab. Reading it here sent users
            // straight to the terminal "your firewall is blocking media" state - with auto-reconnect
            // switched off - on their first ICE failure, without relay ever having been attempted.
            const alreadyRelaying = wasMediaRelayPolicyApplied();

            if (!alreadyRelaying) {
                // First failure: we have not yet tried a relay-only path. Force TURN relay and retry.
                logger.warn(`ICE failed (${err}); retrying over TURN relay.`);
                _forceRelayMediaFallback(`conference-failure-${err}`);
                _reconnectConferenceInPage(`conference-failure-${err}`);

                break;
            }

            // Terminal case: ICE failed again with iceTransportPolicy=relay already in force, so every
            // path this client can offer has been exhausted - direct UDP, TURN/UDP, TURN/TCP and
            // TURN/TLS:443 all failed. Retrying cannot help; the remaining causes are outside the app
            // (firewall/proxy blocking UDP and TCP 443 to the TURN host, or DPI stripping the traffic).
            // Stop the loop and tell the user something they can act on.
            logger.error(`ICE failed (${err}) with TURN relay already forced - all media paths exhausted.`);

            APP.store.dispatch(transportStateChanged({
                diagnosticsSupported: true,
                mediaStatus: 'blocked',
                reason: `all-media-paths-exhausted-${err}`,
                relayProtocol: 'exhausted',
                signalingStatus: 'connected',
                source: 'ice-failed-after-relay',
                userVisibleSeverity: 'error',
                userVisibleType: 'media-blocked',
                websocketStatus: 'connected'
            }));

            APP.conference.autoReconnectSuppressed = true;
            APP.store.dispatch(showErrorNotification({
                descriptionKey: 'notify.firewallBlockedDescription',
                titleKey: 'notify.firewallBlockedTitle'
            }, NOTIFICATION_TIMEOUT_TYPE.STICKY));

            break;
        }

        case MHConferenceErrors.FOCUS_LEFT:
        case MHConferenceErrors.OFFER_ANSWER_FAILED:
            APP.store.dispatch(conferenceWillLeave(room));

            // FIXME the conference should be stopped by the library and not by
            // the app. Both the errors above are unrecoverable from the library
            // perspective.
            room.leave().then(() => connection.disconnect());
            break;

        case MHConferenceErrors.CONFERENCE_MAX_USERS:
            connection.disconnect();
            APP.UI.notifyMaxUsersLimitReached();
            break;

        case MHConferenceErrors.INCOMPATIBLE_SERVER_VERSIONS:
            APP.store.dispatch(reloadWithStoredParams());
            break;

        case MHConferenceErrors.PASSWORD_REQUIRED:
            // Password required error is handled by the room-lock middleware
            // Don't treat this as an unhandled error since it's expected behavior
            //  logger.info('Conference requires password - handled by room-lock middleware');
            break;

        case MHConferenceErrors.MEMBERS_ONLY_ERROR:
            // Members-only error is handled by the lobby middleware.
            // Avoid rejecting the connector promise and triggering webpack overlay.
            break;

        case MHConferenceErrors.DISPLAY_NAME_REQUIRED:
            // Display name required error is handled by display-name/conference middleware.
            // Avoid rejecting the connector promise and triggering webpack overlay.
            break;

        default:
            this._handleConferenceFailed(err, ...params);
        }
    }

    /**
     *
     */
    _unsubscribe() {
        room.off(
            MHConferenceEvents.CONFERENCE_JOINED,
            this._handleConferenceJoined
        );
        room.off(
            MHConferenceEvents.CONFERENCE_FAILED,
            this._onConferenceFailed
        );
        if (this.reconnectTimeout !== null) {
            clearTimeout(this.reconnectTimeout);
        }
        AuthHandler.closeAuth();
    }

    /**
     *
     */
    _handleConferenceJoined() {
        this._unsubscribe();
        this._resolve();
    }

    /**
     *
     */
    connect(password) {
        // Join the room immediately with the password (if any).
        // Passing the password here avoids the CONFERENCE_FAILED → PASSWORD_REQUIRED
        // round-trip that previously caused React component unmount/remount races
        // (blank screen, participants not visible) when switching breakout rooms
        // with a passcode and the camera on.
        room.join(password || undefined);
    }
}

/**
 * Disconnects the connection.
 *
 * @returns Resolved Promise. We need this in order to make the Promise.all
 * call in hangup() to resolve when all operations are finished.
 */
function disconnect() {
    const onDisconnected = () => {
        APP.API.notifyConferenceLeft(APP.conference.roomName);

        return Promise.resolve();
    };

    if (!connection) {
        return onDisconnected();
    }

    return connection.disconnect().then(onDisconnected, onDisconnected);
}

/**
 * Handles CONNECTION_FAILED events from lib-meet-hour.
 *
 * @param {MHConnectionError} error - The reported error.
 * @returns {void}
 * @private
 */
function _connectionFailedHandler(error) {
    if (APP.conference.autoReconnectSuppressed) {
        return;
    }

    // A fatal failure of the connection an auto-reconnect has just opened belongs to that reconnect: its attempt
    // fails and it retries, or its window runs out. Handling it below as well would start an in-page reconnect
    // racing the one already running.
    if (APP.conference.isReconnecting && isFatalMHConnectionError(error)) {
        return;
    }

    if (error === MHConnectionErrors.CONNECTION_DROPPED_ERROR
            && !APP.conference.isReconnecting
            && !APP.conference.autoReconnectSuppressed) {
        logger.warn('[AutoReconnect] Connection dropped — initiating auto-reconnect...');
        APP.conference.isReconnecting = true;
        _autoReconnectStartedAt = Date.now();

        // Remove this listener from the OLD connection (it's dead)
        APP.connection.removeEventListener(
            MHConnectionEvents.CONNECTION_FAILED,
            _connectionFailedHandler
        );

        // Show a non-blocking notification
        APP.store.dispatch(showNotification({
            titleKey: 'prejoin.oops',
            descriptionKey: 'dialog.conferenceDisconnectMsg',
            concatText: true,
            maxLines: 2
        }, NOTIFICATION_TIMEOUT_TYPE.STICKY));

        // Capture current mute states before teardown
        const localTracks = getLocalTracks(APP.store.getState()['features/base/tracks']);
        const isAudioMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.AUDIO);
        const isVideoMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.VIDEO);
        const roomName = APP.conference.roomName;

        // Begin reconnection attempts
        _armAutoReconnectWindowLimit();
        _attemptReconnection(roomName, {
            startWithAudioMuted: isAudioMuted,
            startWithVideoMuted: isVideoMuted
        }, 0);

        return; // Skip the normal fatal error handling below
    }

    // Original fatal error handling for non-dropped errors
    if (isFatalMHConnectionError(error)) {
        // Reconnect over the transport that was working. A websocket that established and later died is not a
        // blocked websocket; forcing BOSH here moved the tab to BOSH long-polling for the rest of the session
        // (runtimeAllowWebSocketReturn is off), which is far less reliable on the same unstable network. A network
        // that genuinely blocks websockets is still caught by the BOSH fallback in connection.js on the new connect.
        APP.connection.removeEventListener(
            MHConnectionEvents.CONNECTION_FAILED,
            _connectionFailedHandler
        );

        if (config?.bosh) {
            _reconnectConferenceInPage('fatal-xmpp-failure');

            return;
        }

        if (room) {
            APP.store.dispatch(conferenceWillLeave(room));
            room.leave();
        }
    }
}

/**
 * Clears the sticky auto-reconnect status notification if present.
 *
 * @returns {void}
 */
function _clearAutoReconnectStatusNotification() {
    if (typeof _autoReconnectNotificationUid !== 'undefined') {
        APP.store.dispatch(hideNotification(_autoReconnectNotificationUid));
        _autoReconnectNotificationUid = undefined;
    }
}

/**
 * Shows a sticky reconnect status message while preserving only one instance.
 *
 * @param {string} description - Message text.
 * @returns {void}
 */
function _showAutoReconnectStatus(description) {
    _clearAutoReconnectStatusNotification();

    const action = APP.store.dispatch(showNotification({
        titleKey: 'connection.CONNFAIL',
        description,
        concatText: true,
        maxLines: 2
    }, NOTIFICATION_TIMEOUT_TYPE.STICKY));

    _autoReconnectNotificationUid = action?.uid;
}

/**
 * Clears reconnect timers and status state for a clean lifecycle reset.
 *
 * @returns {void}
 */
function _clearAutoReconnectLifecycleState() {
    if (_autoReconnectWindowTimer) {
        clearTimeout(_autoReconnectWindowTimer);
        _autoReconnectWindowTimer = undefined;
    }

    if (_autoReconnectCountdownTimer) {
        clearInterval(_autoReconnectCountdownTimer);
        _autoReconnectCountdownTimer = undefined;
    }

    _autoReconnectStartedAt = 0;
    _clearAutoReconnectStatusNotification();
}

/**
 * Starts the terminal countdown shown after reconnect window exhaustion.
 *
 * @returns {void}
 */
function _startAutoReconnectFailureCountdown() {
    let remainingSeconds = AUTO_RECONNECT_COUNTDOWN_SECONDS;

    _showAutoReconnectStatus(
        `Connection Lost. Please rejoin the call. Refreshing the page in ${remainingSeconds}...`
    );

    _autoReconnectCountdownTimer = setInterval(() => {
        remainingSeconds -= 1;

        if (remainingSeconds <= 0) {
            if (_autoReconnectCountdownTimer) {
                clearInterval(_autoReconnectCountdownTimer);
                _autoReconnectCountdownTimer = undefined;
            }

            _clearAutoReconnectStatusNotification();
            APP.store.dispatch(reloadNow(AUTO_RECONNECT_RELOAD_HASH_OVERRIDES));

            return;
        }

        _showAutoReconnectStatus(
            `Connection Lost. Please rejoin the call. Refreshing the page in ${remainingSeconds}...`
        );
    }, 1000);
}

/**
 * Handles expiration of the 2-minute reconnect window.
 *
 * @returns {void}
 */
function _handleAutoReconnectWindowExceeded() {
    if (!APP.conference.isReconnecting) {
        return;
    }

    logger.warn('[AutoReconnect] Reconnect window exceeded 2 minutes; forcing page refresh countdown.');

    APP.conference.isReconnecting = false;
    APP.conference.autoReconnectSuppressed = true;

    if (_autoReconnectWindowTimer) {
        clearTimeout(_autoReconnectWindowTimer);
        _autoReconnectWindowTimer = undefined;
    }

    _startAutoReconnectFailureCountdown();
}

/**
 * Arms the reconnect-window timeout guard.
 *
 * @returns {void}
 */
function _armAutoReconnectWindowLimit() {
    if (_autoReconnectWindowTimer) {
        clearTimeout(_autoReconnectWindowTimer);
    }

    _autoReconnectWindowTimer = setTimeout(
        _handleAutoReconnectWindowExceeded,
        MAX_AUTO_RECONNECT_DURATION_MS
    );
}

/**
 * Resolves current online status in a platform-safe way.
 *
 * @returns {boolean}
 */
function _isOnline() {
    const onlineStateFromRedux = APP.store?.getState()?.['features/base/net-info']?.isOnline;

    if (typeof onlineStateFromRedux === 'boolean') {
        return onlineStateFromRedux;
    }

    if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
        return navigator.onLine;
    }

    return true;
}

/**
 * Returns a Promise that resolves when the browser reports online status.
 *
 * @returns {Promise<void>}
 * @private
 */
function _waitForOnline() {
    return new Promise(resolve => {
        if (_isOnline()) {
            resolve();

            return;
        }

        if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
            logger.log('[AutoReconnect] Waiting for network (native polling)...');
            const pollTimer = setInterval(() => {
                if (_isOnline()) {
                    clearInterval(pollTimer);
                    logger.log('[AutoReconnect] Network is back online.');
                    resolve();
                }
            }, 1000);

            return;
        }

        logger.log('[AutoReconnect] Waiting for network to come back online...');
        const handler = () => {
            window.removeEventListener('online', handler);
            logger.log('[AutoReconnect] Network is back online.');
            resolve();
        };

        window.addEventListener('online', handler);
    });
}

/**
 * Attempts to reconnect to the conference with retry and exponential backoff.
 *
 * @param {string} roomName - The room name to rejoin.
 * @param {Object} muteState - The audio/video mute states to preserve.
 * @param {number} attempt - Current attempt number (0-indexed).
 * @returns {void}
 * @private
 */
async function _attemptReconnection(roomName, muteState, attempt) {
    const BACKOFF_MS = [ 5000, 10000, 20000 ];
    const elapsed = Date.now() - _autoReconnectStartedAt;

    if (!APP.conference.isReconnecting || APP.conference.autoReconnectSuppressed) {
        return;
    }

    if (_autoReconnectStartedAt > 0 && elapsed >= MAX_AUTO_RECONNECT_DURATION_MS) {
        _handleAutoReconnectWindowExceeded();

        return;
    }

    try {
        // 1. Wait for network connectivity
        await _waitForOnline();

        // 2. Wait backoff delay to let the network stabilize
        const delay = BACKOFF_MS[attempt] || 5000;

        logger.log(`[AutoReconnect] Attempt ${attempt + 1} — waiting ${delay}ms...`);
        await new Promise(r => setTimeout(r, delay));

        if (!APP.conference.isReconnecting || APP.conference.autoReconnectSuppressed) {
            return;
        }

        if (_autoReconnectStartedAt > 0
            && Date.now() - _autoReconnectStartedAt >= MAX_AUTO_RECONNECT_DURATION_MS) {
            _handleAutoReconnectWindowExceeded();

            return;
        }

        // 3. Leave old room best-effort (no disconnect — connection is already dead). Not through leaveRoom(): that
        //    is the user's leave and ends this reconnect - it clears isReconnecting (so the old room's
        //    CONFERENCE_LEFT is no longer suppressed and resets the UI), sets autoReconnectSuppressed (so a failure
        //    below never retries) and clears the reconnect window. Otherwise the same teardown as leaveRoom(): the
        //    probe is stopped so it cannot start an in-page reconnect of its own alongside this one, and
        //    CONFERENCE_WILL_LEAVE is what drops the old room's participants from the store.
        _stopRuntimeWebSocketRecoveryProbe();

        if (room) {
            APP.store.dispatch(conferenceWillLeave(room));
        }

        if (room && room.isJoined()) {
            APP.conference._rememberLocalTracksBeforeLeave();
            await room.leave().catch(leaveErr => {
                logger.warn('[AutoReconnect] Leaving the old room failed:', leaveErr);
            });
        }

        // 4. Disconnect old XMPP connection if it still exists
        if (connection) {
            try {
                await connection.disconnect();
            } catch (disconnectErr) {
                logger.warn('[AutoReconnect] Old connection.disconnect() rejected:', disconnectErr);
            }
            connection = null;
            APP.connection = null;
        }

        // 5. Open a fresh XMPP connection (mirrors what startConference does)
        logger.log('[AutoReconnect] Opening fresh XMPP connection...');
        const con = await openConnection({ roomName });

        // 6. Wire up the new connection (mirrors startConference)
        con.addEventListener(
            MHConnectionEvents.CONNECTION_FAILED,
            _connectionFailedHandler
        );
        APP.connection = connection = con;

        // 7. Rejoin the room using the breakout-room pattern
        logger.log('[AutoReconnect] Joining room...');
        await APP.conference.joinRoom(roomName, muteState);

        // 8. Success! Clear the reconnecting flag and notify
        APP.conference.isReconnecting = false;
        APP.conference.autoReconnectSuppressed = false;
        _clearAutoReconnectLifecycleState();
        APP.store.dispatch(localParticipantConnectionStatusChanged(
            MHParticipantConnectionStatus.ACTIVE
        ));
        APP.store.dispatch(showNotification({
            titleKey: 'prejoin.oops',
            descriptionKey: 'dialog.oopsReconnect',
            concatText: true,
            maxLines: 2
        }, NOTIFICATION_TIMEOUT_TYPE.SHORT));

        logger.log('[AutoReconnect] Successfully reconnected!');
    } catch (err) {
        logger.warn(`[AutoReconnect] Attempt ${attempt + 1} failed:`, err);

        if (!APP.conference.isReconnecting || APP.conference.autoReconnectSuppressed) {
            return;
        }

        _attemptReconnection(roomName, muteState, attempt + 1);
    }
}

export default {
    /**
     * Flag used to delay modification of the muted status of local media tracks
     * until those are created (or not, but at that point it's certain that
     * the tracks won't exist).
     */
    _localTracksInitialized: false,

    /**
     * The local tracks of the room last left, kept for {@link _resetLocalMediaForRejoin}. Leaving detaches them from
     * the conference and the TRACK_REMOVED handler then drops them from the store without disposing them, so this is
     * the only reference left through which their devices can be released.
     */
    _localTracksOfLeftRoom: [],

    /**
     * Flag indicating that an automatic reconnection is in progress.
     * When true, CONFERENCE_LEFT and CONFERENCE_FAILED events from the
     * old (dead) conference should be suppressed to prevent UI teardown.
     *
     * @type {boolean}
     */
    isReconnecting: false,

    /**
     * Prevents automatic reconnection after intentional exits (kick/hangup).
     *
     * @type {boolean}
     */
    autoReconnectSuppressed: false,

    isSharingScreen: false,

    /**
     * The local audio track (if any).
     * FIXME tracks from redux store should be the single source of truth.
     *
     * @type {MHLocalTrack|null}
     */
    localAudio: null,

    /**
     * The local presenter video track (if any).
     *
     * @type {MHLocalTrack|null}
     */
    localPresenterVideo: null,

    /**
     * The local video track (if any).
     * FIXME tracks from redux store should be the single source of truth, but
     * more refactoring is required around screen sharing ('localVideo' usages).
     *
     * @type {MHLocalTrack|null}
     */
    localVideo: null,

    /**
     * Returns an object containing a promise which resolves with the created tracks &
     * the errors resulting from that process.
     *
     * @returns {Promise<MHLocalTrack[]>, Object}
     */
    createInitialLocalTracks(options = {}) {
        const errors = {};

        // Always get a handle on the audio input device so that we have statistics (such as "No audio input" or
        // "Are you trying to speak?" ) even if the user joins the conference muted.
        const initialDevices = config.disableInitialGUM ? [] : [ 'audio' ];
        const requestedAudio = !config.disableInitialGUM;
        let requestedVideo = false;

        if (
            !config.disableInitialGUM
            && !options.startAudioOnly
        ) {
            initialDevices.push('video');
            requestedVideo = true;
        }

        // This runs for every conference created in the page, and mediaDevices is a singleton: remove the previous
        // run's listeners so the prompt/slow-gUM overlays are not dispatched once per past conference.
        this._onPermissionPromptIsShown && MeetHourJS.mediaDevices.removeEventListener(
            MHMediaDevicesEvents.PERMISSION_PROMPT_IS_SHOWN, this._onPermissionPromptIsShown);
        this._onSlowGetUserMedia && MeetHourJS.mediaDevices.removeEventListener(
            MHMediaDevicesEvents.SLOW_GET_USER_MEDIA, this._onSlowGetUserMedia);
        this._onPermissionPromptIsShown = undefined;

        if (!config.disableInitialGUM) {
            this._onPermissionPromptIsShown = browserName =>
                APP.store.dispatch(mediaPermissionPromptVisibilityChanged(true, browserName));
            MeetHourJS.mediaDevices.addEventListener(
                MHMediaDevicesEvents.PERMISSION_PROMPT_IS_SHOWN, this._onPermissionPromptIsShown);
        }

        this._onSlowGetUserMedia = () => APP.store.dispatch(toggleSlowGUMOverlay(true));
        MeetHourJS.mediaDevices.addEventListener(
            MHMediaDevicesEvents.SLOW_GET_USER_MEDIA, this._onSlowGetUserMedia);

        let tryCreateLocalTracks;

        // On Electron there is no permission prompt for granting permissions. That's why we don't need to
        // spend much time displaying the overlay screen. If GUM is not resolved withing 15 seconds it will
        // probably never resolve.
        // For browsers, we use a more conservative timeout but with retry logic to handle network issues
        let timeout = browser.isElectron() ? 15000 : 45000; // Reduced from 60s to 45s for faster retries

        // Adjust timeout based on connection quality if available
        if (typeof navigator !== 'undefined' && navigator.connection) {
            // eslint-disable-next-line no-shadow
            const connection = navigator.connection;
            const effectiveType = connection.effectiveType;

            // Increase timeout for slower connections
            if (effectiveType === 'slow-2g' || effectiveType === '2g') {
                timeout = browser.isElectron() ? 20000 : 60000;

                //  logger.info('Slow connection detected, increasing timeout to', timeout);
            } else if (effectiveType === '3g') {
                timeout = browser.isElectron() ? 18000 : 50000;

                //  logger.info('3G connection detected, adjusting timeout to', timeout);
            }
        }

        // FIXME is there any simpler way to rewrite this spaghetti below ?
        if (options.startScreenSharing) {
            tryCreateLocalTracks = this._createDesktopTrack()
                .then(([ desktopStream ]) => {
                    if (!requestedAudio) {
                        return [ desktopStream ];
                    }

                    return createLocalTracksF({
                        devices: [ 'audio' ],
                        timeout,
                        firePermissionPromptIsShownEvent: true,
                        fireSlowPromiseEvent: true
                    })
                        .then(([ audioStream ]) => [ desktopStream, audioStream ])
                        .catch(error => {
                            errors.audioOnlyError = error;

                            return [ desktopStream ];
                        });
                })
                .catch(error => {
                    logger.error('Failed to obtain desktop stream', error);
                    errors.screenSharingError = error;

                    return requestedAudio
                        ? createLocalTracksF({
                            devices: [ 'audio' ],
                            timeout,
                            firePermissionPromptIsShownEvent: true,
                            fireSlowPromiseEvent: true
                        })
                        : [];
                })
                .catch(error => {
                    errors.audioOnlyError = error;

                    return [];
                });
        } else if (!requestedAudio && !requestedVideo) {
            // Resolve with no tracks
            tryCreateLocalTracks = Promise.resolve([]);
        } else {
            tryCreateLocalTracks = createLocalTracksF({
                devices: initialDevices,
                timeout,
                firePermissionPromptIsShownEvent: true,
                fireSlowPromiseEvent: true
            }, APP.store)
                .catch(err => {
                    if (requestedAudio && requestedVideo) {
                        // Try audio only...
                        errors.audioAndVideoError = err;

                        if (
                            err.name === MHTrackErrors.TIMEOUT
                            && !browser.isElectron()
                        ) {
                            // In this case we expect that the permission prompt is still visible. There is no point of
                            // executing GUM with different source. Also at the time of writting the following
                            // inconsistency have been noticed in some browsers - if the permissions prompt is visible
                            // and another GUM is executed the prompt does not change its content but if the user
                            // clicks allow the user action isassociated with the latest GUM call.
                            errors.audioOnlyError = err;
                            errors.videoOnlyError = err;

                            return [];
                        }

                        return createLocalTracksF({
                            devices: [ 'audio' ],
                            timeout,
                            firePermissionPromptIsShownEvent: true,
                            fireSlowPromiseEvent: true
                        });
                    } else if (requestedAudio && !requestedVideo) {
                        errors.audioOnlyError = err;

                        return [];
                    } else if (requestedVideo && !requestedAudio) {
                        errors.videoOnlyError = err;

                        return [];
                    }
                    logger.error('Should never happen');
                })
                .catch(err => {
                    // Log this just in case...
                    if (!requestedAudio) {
                        logger.error('The impossible just happened', err);
                    }
                    errors.audioOnlyError = err;

                    // Try video only...
                    return requestedVideo
                        ? createLocalTracksF({
                            devices: [ 'video' ],
                            firePermissionPromptIsShownEvent: true,
                            fireSlowPromiseEvent: true
                        })
                        : [];
                })
                .catch(err => {
                    // Log this just in case...
                    if (!requestedVideo) {
                        logger.error('The impossible just happened', err);
                    }
                    errors.videoOnlyError = err;

                    return [];
                });
        }

        // Hide the permissions prompt/overlay as soon as the tracks are
        // created. Don't wait for the connection to be made, since in some
        // cases, when auth is rquired, for instance, that won't happen until
        // the user inputs their credentials, but the dialog would be
        // overshadowed by the overlay.
        tryCreateLocalTracks.then(tracks => {
            APP.store.dispatch(toggleSlowGUMOverlay(false));
            APP.store.dispatch(mediaPermissionPromptVisibilityChanged(false));

            return tracks;
        });

        return {
            tryCreateLocalTracks,
            errors
        };
    },

    /**
     * Displays error notifications according to the state carried by {@code errors} object returned
     * by {@link createInitialLocalTracks}.
     *
     * @param {Object} errors - the errors (if any) returned by {@link createInitialLocalTracks}.
     *
     * @returns {void}
     * @private
     */
    _displayErrorsForCreateInitialLocalTracks(errors) {
        const {
            audioAndVideoError,
            audioOnlyError,
            screenSharingError,
            videoOnlyError
        } = errors;

        // FIXME If there will be microphone error it will cover any screensharing dialog, but it's still better than in
        // the reverse order where the screensharing dialog will sometimes be closing the microphone alert
        // ($.prompt.close(); is called). Need to figure out dialogs chaining to fix that.
        if (screenSharingError) {
            this._handleScreenSharingError(screenSharingError);
        }
        if (audioAndVideoError || audioOnlyError) {
            if (audioOnlyError || videoOnlyError) {
                // If both requests for 'audio' + 'video' and 'audio' only failed, we assume that there are some
                // problems with user's microphone and show corresponding dialog.
                APP.store.dispatch(notifyMicError(audioOnlyError));
                APP.store.dispatch(notifyCameraError(videoOnlyError));
            } else {
                // If request for 'audio' + 'video' failed, but request for 'audio' only was OK, we assume that we had
                // problems with camera and show corresponding dialog.
                APP.store.dispatch(notifyCameraError(audioAndVideoError));
            }
        }
    },

    /**
     * Creates local media tracks and connects to a room. Will show error
     * dialogs in case accessing the local microphone and/or camera failed. Will
     * show guidance overlay for users on how to give access to camera and/or
     * microphone.
     *
     * @param {string} roomName
     * @param {Object} options
     * @param {boolean} options.startAudioOnly=false - If <tt>true</tt> then
     * only audio track will be created and the audio only mode will be turned
     * on.
     * @param {boolean} options.startScreenSharing=false - If <tt>true</tt>
     * should start with screensharing instead of camera video.
     * @param {boolean} options.startWithAudioMuted - Will start the conference
     * without any audio tracks.
     * @param {boolean} options.startWithVideoMuted - Will start the conference
     * without any video tracks.
     * @returns {Promise.<MHLocalTrack[], MHConnection>}
     */
    createInitialLocalTracksAndConnect(roomName, options = {}) {
        const { tryCreateLocalTracks, errors }
            = this.createInitialLocalTracks(options);

        return Promise.all([ tryCreateLocalTracks, connect(roomName) ]).then(
            ([ tracks, con ]) => {
                this._displayErrorsForCreateInitialLocalTracks(errors);

                return [ tracks, con ];
            }
        );
    },

    startConference(con, tracks) {
        this.autoReconnectSuppressed = false;
        _clearAutoReconnectLifecycleState();

        tracks.forEach(track => {
            if (
                (track.isAudioTrack() && this.isLocalAudioMuted())
                || (track.isVideoTrack() && this.isLocalVideoMuted())
            ) {
                const mediaType = track.getType();

                sendAnalytics(createTrackMutedEvent(mediaType, 'initial mute'));
                track.mute();
            }
        });
        logger.log(`Initialized with ${tracks.length} local tracks`);

        this._localTracksInitialized = true;
        con.addEventListener(
            MHConnectionEvents.CONNECTION_FAILED,
            _connectionFailedHandler
        );
        APP.connection = connection = con;

        if (_consumeDebugFlagOncePerSession(DEBUG_FLAG_FORCE_WEBSOCKET_CLOSED)) {
            window.setTimeout(() => {
                _handleWebSocketClosedScenario('debug-websocket-closed');
            }, 900);
        }

        this._createRoom(tracks);

        // if user didn't give access to mic or camera or doesn't have
        // them at all, we mark corresponding toolbar buttons as muted,
        // so that the user can try unmute later on and add audio/video
        // to the conference
        if (!tracks.find(t => t.isAudioTrack())) {
            this.setAudioMuteStatus(true);
        }

        if (!tracks.find(t => t.isVideoTrack())) {
            this.setVideoMuteStatus(true);
        }

        if (config.iAmRecorder) {
            this.recorder = new Recorder();
        }

        if (config.startSilent) {
            sendAnalytics(createStartSilentEvent());
            APP.store.dispatch(
                showNotification({
                    descriptionKey: 'notify.startSilentDescription',
                    titleKey: 'notify.startSilentTitle'
                })
            );
        }

        // XXX The API will take care of disconnecting from the XMPP
        // server (and, thus, leaving the room) on unload.
        return new Promise((resolve, reject) => {
            new ConferenceConnector(resolve, reject).connect();
        });
    },

    /**
     * Open new connection and join the conference when prejoin page is not enabled.
     * If prejoin page is enabled open an new connection in the background
     * and create local tracks.
     *
     * @param {{ roomName: string }} roomName
     *
     */
    prejoinConnect(roomName) {
        _connectionPromise = connect(roomName).then(c => {
            // we want to initialize it early, in case of errors to be able
            // to gather logs
            APP.connection = c;

            return c;
        });
    },

    /**
     * Open new connection and join the conference when prejoin page is not enabled.
     * If prejoin page is enabled open an new connection in the background
     * and create local tracks.
     *
     * @param {{ roomName: string }} options
     * @returns {Promise}
     */
    async init({ roomName }) {
        // Clear any stale connection error message from a previous join attempt
        // so the loader does not flash the old error before the new connect() starts.
        APP.store.dispatch(setPrejoinJoinError(''));
        this.autoReconnectSuppressed = false;
        _clearAutoReconnectLifecycleState();
        _startRuntimeWebSocketRecoveryProbe();

        const initialOptions = {
            startAudioOnly: config?.startAudioOnly,
            startScreenSharing: config.startScreenSharing,
            startWithAudioMuted:
                getStartWithAudioMuted(APP.store.getState())
                || config.startSilent
                || isUserInteractionRequiredForUnmute(APP.store.getState()),
            startWithVideoMuted:
                getStartWithVideoMuted(APP.store.getState())
                || isUserInteractionRequiredForUnmute(APP.store.getState())
        };

        this.roomName = roomName;

        try {
            // Initialize the device list first. This way, when creating tracks
            // based on preferred devices, loose label matching can be done in
            // cases where the exact ID match is no longer available, such as
            // when the camera device has switched USB ports.
            // when in startSilent mode we want to start with audio muted
            await this._initDeviceList();
        } catch (error) {
            logger.warn('initial device list initialization failed', error);
        }

        if (isPrejoinPageEnabled(APP.store.getState())) {
            // _connectionPromise = connect(roomName).then(c => {
            //     // we want to initialize it early, in case of errors to be able
            //     // to gather logs
            //     APP.connection = c;

            //     return c;
            // });

            APP.store.dispatch(makePrecallTest(this._getConferenceOptions()));

            const { tryCreateLocalTracks, errors }
                = this.createInitialLocalTracks(initialOptions);
            const tracks = await tryCreateLocalTracks;

            // Initialize device list a second time to ensure device labels
            // get populated in case of an initial gUM acceptance; otherwise
            // they may remain as empty strings.
            this._initDeviceList(true);

            if (isPrejoinPageVisible(APP.store.getState())) {
                return APP.store.dispatch(initPrejoin(tracks, errors));
            }

            logger.debug(
                'Prejoin screen no longer displayed at the time when tracks were created'
            );

            this._displayErrorsForCreateInitialLocalTracks(errors);

            return this._setLocalAudioVideoStreams(tracks);
        }

        const [ tracks, con ] = await this.createInitialLocalTracksAndConnect(
            roomName,
            initialOptions
        );

        let localTracks = tracks;

        this._initDeviceList(true);

        const state = APP.store.getState();

        if (initialOptions.startWithAudioMuted) {
            // Diagnostic: log whenever the audio track is filtered out by startWithAudioMuted.
            // On a deep-link rejoin on Windows Chrome, isUserInteractionRequiredForUnmute returns
            // true and this branch silently drops the audio track from the local track list. If
            // the user then clicks unmute and useAudioStream never reaches a healthy conference,
            // remote participants receive silence while the local indicator shows unmuted. The
            // log lets us tell this from the more common "user actually wanted to start muted"
            // path in production.
            const hadAudio = localTracks.some(track => track.getType() === MEDIA_TYPE.AUDIO);
            const filteredAudioCount
                = localTracks.length
                    - localTracks.filter(track => track.getType() !== MEDIA_TYPE.AUDIO).length;

            if (hadAudio) {
                logger.warn(
                    '[DIAG] startWithAudioMuted filtered '
                    + `${filteredAudioCount} local audio track(s) before startConference. Source:`
                    + ` startSilent=${Boolean(config.startSilent)},`
                    + ` isUserInteractionRequiredForUnmute=${Boolean(
                        isUserInteractionRequiredForUnmute(state))}`);
            }
            localTracks = localTracks.filter(track => track.getType() !== MEDIA_TYPE.AUDIO);
        }

        const hasDisplayName = Boolean(
            state['features/base/settings']?.displayName
            || getLocalParticipant(state)?.name
        );

        if (!hasDisplayName && isDisplayNameRequired(state)) {
            await new Promise(resolve => {
                APP.store.dispatch(openDisplayNamePrompt(resolve));
            });
        }

        return this.startConference(con, localTracks);
    },

    /**
     * Joins conference after the tracks have been configured in the prejoin screen.
     *
     * @param {Object[]} tracks - An array with the configured tracks.
     * @returns {Promise}
     */
    async prejoinStart(tracks) {
        const configuredTimeout = Number(config?.prejoinJoinTimeoutMs);
        const timeoutMs = Number.isFinite(configuredTimeout) && configuredTimeout > 0
            ? configuredTimeout
            : DEFAULT_PREJOIN_JOIN_TIMEOUT_MS;

        const timeoutPromise = new Promise((resolve, reject) => {
            setTimeout(() => reject(new Error(PREJOIN_JOIN_TIMEOUT_ERROR)), timeoutMs);
        });

        try {
            const con = await Promise.race([ _connectionPromise, timeoutPromise ]);

            return this.startConference(con, tracks);
        } catch (error) {
            // connect() races a timeout of its own and rejects with CONNFAIL_TIMEOUT, and it always wins this race
            // because its timer starts when the user clicks join, before this one. Matching only the local
            // PREJOIN_JOIN_TIMEOUT sent every real connect timeout down the generic path below, so the
            // websocket -> BOSH failover never ran for the very case it was written for and the participant was
            // returned to the prejoin screen to retry over the same transport that had just failed.
            const isJoinTimeout = error === CONNECTION_TIMEOUT_ERROR
                || error?.message === CONNECTION_TIMEOUT_ERROR
                || error?.message === PREJOIN_JOIN_TIMEOUT_ERROR;

            if (!isJoinTimeout) {
                logger.error('Prejoin join attempt failed.', error);
                APP.store.dispatch(setPrejoinPageVisibility(true));

                return;
            }

            logger.warn(`Prejoin join attempt timed out (${error?.message}); switching to BOSH preference.`);

            _forceBoshTransportPreference('prejoin-timeout');

            const connectionFailedText = i18next.t('connection.CONNFAIL');

            // connect() already reports its own timeout to the user through UI.notifyConnectionFailed, so only
            // the timeout raised here needs a notification.
            if (error?.message === PREJOIN_JOIN_TIMEOUT_ERROR) {
                APP.store.dispatch(showErrorNotification({
                    titleKey: 'connection.CONNFAIL',
                    description: connectionFailedText
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            }
            APP.store.dispatch(setPrejoinJoinError(connectionFailedText));
            APP.store.dispatch(setPrejoinPageVisibility(true));

            return;
        }
    },

    /**
     * Check if id is id of the local user.
     *
     * @param {string} id - Id to check.
     * @returns {boolean}
     */
    isLocalId(id) {
        return this.getMyUserId() === id;
    },

    /**
     * Tells whether the local video is muted or not.
     *
     * @returns {boolean}
     */
    isLocalVideoMuted() {
        // If the tracks are not ready, read from base/media state
        return this._localTracksInitialized
            ? isLocalCameraTrackMuted(
                  APP.store.getState()['features/base/tracks']
            )
            : isVideoMutedByUser(APP.store);
    },

    /**
     * Verify if there is an ongoing system audio sharing session and apply to the provided track
     * as a AudioMixer effect.
     *
     * @param {*} localAudioTrack - track to which system audio track will be applied as an effect, most likely
     * microphone local audio track.
     */
    async _maybeApplyAudioMixerEffect(localAudioTrack) {

        // At the time of writing this comment there were two separate flows for toggling screen-sharing
        // and system audio sharing, the first is the legacy method using the functionality from conference.js
        // the second is used when both sendMultipleVideoStreams and sourceNameSignaling flags are set to true.
        // The second flow uses functionality from base/conference/middleware.web.js.
        // We check if system audio sharing was done using the first flow by verifying this._desktopAudioStream and
        // for the second by checking 'features/screen-share' state.
        const { desktopAudioTrack } = APP.store.getState()['features/screen-share'];
        const currentDesktopAudioTrack = this._desktopAudioStream || desktopAudioTrack;

        // If system audio is already being sent, mix it with the provided audio track.
        if (currentDesktopAudioTrack) {
            // In case system audio sharing was done in the absence of an initial mic audio track, there is no
            // AudioMixerEffect so we have to remove system audio track from the room before setting it as an effect.
            await room.replaceTrack(currentDesktopAudioTrack, null);
            this._mixerEffect = new AudioMixerEffect(currentDesktopAudioTrack);
            logger.debug('Mixing new audio track with existing screen audio track.');
            await localAudioTrack.setEffect(this._mixerEffect);
        }
    },

    /**
     * Simulates toolbar button click for audio mute. Used by shortcuts and API.
     *
     * @param {boolean} mute - True for mute and false for unmute.
     * @param {boolean} [showUI] - When set to false will not display any error
     * dialogs in case of media permissions error.
     */
    muteAudio(mute, showUI = true) {
        if (!mute && (isUserInteractionRequiredForUnmute(APP.store.getState())
                || isLocalParticipantForceMutedAudio(APP.store.getState()))) {
            logger.error('Unmuting audio is blocked: user interaction required or local participant is force muted');

            return;
        }

        // Not ready to modify track's state yet
        if (!this._localTracksInitialized) {
            // This will only modify base/media.audio.muted which is then synced
            // up with the track at the end of local tracks initialization.
            muteLocalAudio(mute);
            this.setAudioMuteStatus(mute);

            return;
        } else if (this.isLocalAudioMuted() === mute) {
            // NO-OP
            return;
        }

        // Take the create-a-new-track path only when there is no usable track to unmute.
        //
        // A moderator clause used to sit here too, sending every moderator unmute down this path even with a
        // perfectly healthy track. It was redundant - the dead-track test below already covers the case it was
        // reaching for, for every participant rather than just moderators - and it was harmful: each unmute ran
        // a fresh gUM and replaced a live track, which is the exact sequence behind the
        // "Cannot add second audio track" and "Replace track failed - no transceiver" reports.
        if (
            (!this.localAudio && !mute)
            || (this.localAudio
                && mute === false
                && (this.localAudio.disposed || !this.localAudio.track || this.localAudio.track.readyState === 'ended'))
        ) {
            const maybeShowErrorDialog = error => {
                showUI && APP.store.dispatch(notifyMicError(error));
            };

            // Pick the device the user actually selected first; only fall back to "no deviceId" (which is what
            // gUM reports as the OS default) if the preferred mic fails or no preference is stored. The
            // previous code skipped straight to the bare gUM, so a user with a stale "default" preference who
            // clicked the toolbar toggle got whatever the OS chose - that mismatch is what left the conference
            // holding a track that no other path could later replace cleanly.
            const state = APP.store.getState();
            const preferredMicId = state['features/base/settings'].userSelectedMicDeviceId;

            const tryCreate = (micDeviceId, isFallback) =>
                createLocalTracksF({
                    devices: [ 'audio' ],
                    micDeviceId
                })
                  .then(([ audioTrack ]) => audioTrack)
                  .catch(error => {
                      // No preference, or the preferred device failed - try without the deviceId hint. Only
                      // after the second attempt fails do we surface an error to the user, otherwise we'd
                      // show "microphone error" for a stale ID that the OS would have happily honoured.
                      if (!isFallback && micDeviceId
                              && (error?.name === 'NotFoundError'
                                  || error?.name === 'OverconstrainedError'
                                  || error?.name === 'NotAllowedError')) {
                          logger.warn(`muteAudio: mic ${micDeviceId} failed (${error?.name}), retrying with system default`);

                          return tryCreate(undefined, true);
                      }

                      throw error;
                  });

            tryCreate(preferredMicId, false)

              // Rollback the audio muted status by using null track
              .catch(error => {
                  maybeShowErrorDialog(error);

                  return null;
              })
              .then(audioTrack => {
                  if (audioTrack) {
                      return this.useAudioStream(audioTrack);
                  }
              });
        } else {
            muteLocalAudio(mute);
        }
    },

    /**
     * Returns whether local audio is muted or not.
     *
     * @returns {boolean}
     */
    isLocalAudioMuted() {
        // If the tracks are not ready, read from base/media state
        return this._localTracksInitialized
            ? isLocalTrackMuted(
                  APP.store.getState()['features/base/tracks'],
                  MEDIA_TYPE.AUDIO
            )
            : Boolean(APP.store.getState()['features/base/media'].audio.muted);
    },

    /**
     * Simulates toolbar button click for audio mute. Used by shortcuts
     * and API.
     *
     * @param {boolean} [showUI] - When set to false will not display any error
     * dialogs in case of media permissions error.
     */
    toggleAudioMuted(showUI = true) {
        this.muteAudio(!this.isLocalAudioMuted(), showUI);
    },


    /**
     * Simulates toolbar button click for video mute. Used by shortcuts and API.
     *
     * @param mute - True for mute and false for unmute.
     * @param {boolean} [showUI] - When set to false will not display any error
     * dialogs in case of media permissions error.
     */
    muteVideo(mute, showUI = true) {
        if (!mute && (isUserInteractionRequiredForUnmute(APP.store.getState())
                || isLocalParticipantForceMutedVideo(APP.store.getState()))) {
            logger.error('Unmuting video is blocked: user interaction required or local participant is force muted');

            return;
        }


        // If not ready to modify track's state yet adjust the base/media
        if (!this._localTracksInitialized) {
            // This will only modify base/media.video.muted which is then synced
            // up with the track at the end of local tracks initialization.
            muteLocalVideo(mute);
            this.setVideoMuteStatus(mute);

            return;
        } else if (this.isLocalVideoMuted() === mute) {
            // NO-OP
            return;
        }

        // Mirrors muteAudio above. The moderator clause this replaces dated from 2024 and had the same two
        // problems: it recreated a healthy track on every moderator unmute, and it helped only moderators, when a
        // track that has genuinely died needs recreating for anyone.
        //
        // Note this tests less than the audio version does, on purpose. Muting camera on web goes through
        // `doesVideoMuteByStreamRemove`, which calls `_setStream(null)` - so a null `track` is the *normal* muted
        // state for video, not a fault, and MHLocalTrack._setMuted already reopens the camera itself on unmute.
        // Treating null as a fault here would send every web video unmute down a second, competing gUM path.
        // Audio mutes with `track.enabled = false` and keeps its track, so there a null track really is broken.
        // While a screenshare is running `this.localVideo` holds the DESKTOP track, so it says nothing about the
        // camera - testing it here would look at a live screenshare and conclude the camera is fine, and an
        // unmute with no camera track at all would never create one. Resolve the camera from the store in that
        // one case, the way `useVideoStream` does; everywhere else keep reading the field, whose null is
        // load-bearing at join time.
        const cameraTrack = this.localVideo?.videoType === 'desktop'
            ? getLocalMHVideoTrack(APP.store.getState())
            : this.localVideo;

        if (
            (!cameraTrack && !mute)
            || (cameraTrack
                && mute === false
                && (cameraTrack.disposed || cameraTrack.track?.readyState === 'ended'))
        ) {
            const maybeShowErrorDialog = error => {
                showUI && APP.store.dispatch(notifyCameraError(error));
            };

            // Try to create local video if there wasn't any.
            // This handles the case when user joined with no video
            // (dismissed screen sharing screen or in audio only mode), but
            // decided to add it later on by clicking on muted video icon or
            // turning off the audio only mode.
            //
            // FIXME when local track creation is moved to react/redux
            // it should take care of the use case described above
            createLocalTracksF({ devices: [ 'video' ] })
                .then(([ videoTrack ]) => videoTrack)
                .catch(error => {
                    // FIXME should send some feedback to the API on error ?
                    maybeShowErrorDialog(error);

                    // Rollback the video muted status by using null track
                    return null;
                })
                .then(videoTrack => {
                    logger.debug(
                        `muteVideo: calling useVideoStream for track: ${videoTrack}`
                    );
                    if (videoTrack) {
                        return this.useVideoStream(videoTrack);
                    }
                });
        } else {
            // FIXME show error dialog if it fails (should be handled by react)
            muteLocalVideo(mute);
        }
    },

    /**
     * Simulates toolbar button click for video mute. Used by shortcuts and API.
     *
     * @param {boolean} [showUI] - When set to false will not display any error
     * dialogs in case of media permissions error.
     */
    toggleVideoMuted(showUI = true) {
        this.muteVideo(!this.isLocalVideoMuted(), showUI);
    },

    /**
     * Retrieve list of ids of conference participants (without local user).
     *
     * @returns {string[]}
     */
    listMembersIds() {
        return room.getParticipants().map(p => p.getId());
    },

    /**
     * Checks whether the participant identified by id is a moderator.
     *
     * @id id to search for participant
     * @returns {boolean} Whether the participant is moderator.
     */
    isParticipantModerator(id) {
        const user = room.getParticipantById(id);

        return user && user.isModerator();
    },

    /** ........
     * Retrieve list of conference participants (without local user).
     *
     * @returns {MHParticipant[]}
     *
     * NOTE: Used by meet-hour-torture!
     */
    listMembers() {
        return room.getParticipants();
    },

    get membersCount() {
        return room.getParticipants().length + 1;
    },

    /**
     * Returns true if the callstats integration is enabled, otherwise returns
     * false.
     *
     * @returns True if the callstats integration is enabled, otherwise returns
     * false.
     */
    isCallstatsEnabled() {
        return room && room.isCallstatsEnabled();
    },

    /**
     * Sends the given feedback through CallStats if enabled.
     *
     * @param overallFeedback - An integer between 1 and 5 indicating the
     * user feedback.
     * @param detailedFeedback - Detailed feedback from the user. Not yet used.
     */
    sendFeedback(overallFeedback, detailedFeedback) {
        return room.sendFeedback(overallFeedback, detailedFeedback);
    },

    /**
     * Get speaker stats that track total dominant speaker time.
     *
     * @returns {Object} A hash with keys being user ids and values being the
     * library's SpeakerStats model used for calculating time as dominant
     * speaker.
     */
    getSpeakerStats() {
        return room.getSpeakerStats();
    },

    /**
     * Returns the connection times stored in the library.
     */
    getConnectionTimes() {
        return room.getConnectionTimes();
    },

    // used by torture currently
    isJoined() {
        return room && room.isJoined();
    },
    getConnectionState() {
        return room && room.getConnectionState();
    },

    /**
     * Obtains current P2P ICE connection state.
     *
     * @returns {string|null} ICE connection state or <tt>null</tt> if there's no
     * P2P connection.
     */
    getP2PConnectionState() {
        return room && room.getP2PConnectionState();
    },

    /** ........
     * Starts P2P (for tests only)
     *
     * @private
     */
    _startP2P() {
        try {
            room && room.startP2PSession();
        } catch (error) {
            logger.error('Start P2P failed', error);
            throw error;
        }
    },

    /** ........
     * Stops P2P (for tests only)
     *
     * @private
     */
    _stopP2P() {
        try {
            room && room.stopP2PSession();
        } catch (error) {
            logger.error('Stop P2P failed', error);
            throw error;
        }
    },

    /**
     * Checks whether or not our connection is currently in interrupted and
     * reconnect attempts are in progress.
     *
     * @returns {boolean} True if the connection is in interrupted state or
     * false otherwise.
     */
    isConnectionInterrupted() {
        return room.isConnectionInterrupted();
    },

    /**
     * Obtains the local display name.
     *
     * @returns {string|undefined}
     */
    getLocalDisplayName() {
        return getDisplayName(this.getMyUserId());
    },

    /**
     * Finds MHParticipant for given id.
     *
     * @param {string} id - Participant's identifier(MUC nickname).
     *
     * @returns {MHParticipant|null} Participant instance for given id or
     * null if not found.
     */
    getParticipantById(id) {
        return room ? room.getParticipantById(id) : null;
    },

    /**
     * Gets the display name foe the <tt>MHParticipant</tt> identified by
     * the given <tt>id</tt>.
     *
     * @param id - {string} The participant's id(MUC nickname/JVB endpoint id).
     *
     * @returns {string} The participant's display name or the default string if
     * absent.
     */
    getParticipantDisplayName(id) {
        const displayName = getDisplayName(id);

        if (displayName) {
            return displayName;
        }
        if (APP.conference.isLocalId(id)) {
            return APP.translation.generateTranslationHTML(
                interfaceConfig.DEFAULT_LOCAL_DISPLAY_NAME
            );
        }

        return interfaceConfig.DEFAULT_REMOTE_DISPLAY_NAME;
    },

    getMyUserId() {
        return room && room.myUserId();
    },

    /**
     * Will be filled with values only when config.debug is enabled.
     * Its used by torture to check audio levels.
     */
    audioLevelsMap: {},

    /** ........
     * Returns the stored audio level (stored only if config.debug is enabled)
     *
     * @param id the id for the user audio level to return (the id value is
     *          returned for the participant using getMyUserId() method)
     */
    getPeerSSRCAudioLevel(id) {
        return this.audioLevelsMap[id];
    },

    /**
     * @returns {number} The number of participants in the conference with at
     * least one track.
     */
    getNumberOfParticipantsWithTracks() {
        return room.getParticipants().filter(p => p.getTracks().length > 0)
            .length;
    },

    /**
     * Returns the stats.
     */
    getStats() {
        return room.connectionQuality.getStats();
    },

    // end used by torture

    /**
     * Download logs, a function that can be called from console while
     * debugging.
     *
     * @param filename - (optional) specify target filename.
     */
    saveLogs(filename = 'meetlog.json') {
        // this can be called from console and will not have reference to this
        // that's why we reference the global var
        const logs = APP.connection.getLogs();

        downloadJSON(logs, filename);
    },

    /**
     * Download app state, a function that can be called from console while debugging.
     * @param filename (optional) specify target filename
     */
    saveState(filename = 'meet-state.json') {
        downloadJSON(APP.store.getState(), filename);
    },

    /**
     * Exposes a Command(s) API on this instance. It is necessitated by (1) the
     * desire to keep room private to this instance and (2) the need of other
     * modules to send and receive commands to and from participants.
     * Eventually, this instance remains in control with respect to the
     * decision whether the Command(s) API of room (i.e. Lib-meet-hour's
     * MHConference) is to be used in the implementation of the Command(s)
     * API of this instance.
     */
    commands: {
        /**
         * Known custom conference commands.
         */
        defaults: commands,

        /**
         * Receives notifications from other participants about commands aka
         * custom events (sent by sendCommand or sendCommandOnce methods).
         *
         * @param command - {String} The name of the command.
         * @param handler - {Function} Handler for the command.
         */
        addCommandListener() {
            // eslint-disable-next-line prefer-rest-params
            room.addCommandListener(...arguments);
        },

        /**
         * Removes command.
         *
         * @param name - {String} The name of the command.
         */
        removeCommand() {
            // eslint-disable-next-line prefer-rest-params
            room.removeCommand(...arguments);
        },

        /**
         * Sends command.
         *
         * @param name - {String} The name of the command.
         * @param values - {Object} With keys and values that will be sent.
         */
        sendCommand() {
            // eslint-disable-next-line prefer-rest-params
            room.sendCommand(...arguments);
        },

        /**
         * Sends command one time.
         *
         * @param name - {String} The name of the command.
         * @param values - {Object} With keys and values that will be sent.
         */
        sendCommandOnce() {
            // eslint-disable-next-line prefer-rest-params
            room.sendCommandOnce(...arguments);
        }
    },

    /**
     * Records the app's local tracks just before the current room is left, see {@link _localTracksOfLeftRoom}.
     *
     * @returns {void}
     */
    _rememberLocalTracksBeforeLeave() {
        const tracks = getLocalTracks(APP.store.getState()['features/base/tracks']).map(track => track.mhTrack);

        this._localTracksOfLeftRoom = [ ...new Set([ ...this._localTracksOfLeftRoom, ...tracks ]) ];
    },

    /**
     * Disposes the app's local tracks once the room has been left: whatever is still in the store, then the tracks
     * {@link _rememberLocalTracksBeforeLeave} recorded, which leaving dropped from the store without disposing.
     *
     * @returns {Promise<void>}
     */
    async _destroyLocalTracksOfLeftRoom() {
        const leftRoomTracks = this._localTracksOfLeftRoom;

        this._localTracksOfLeftRoom = [];

        // Normally nothing is left in the store, but leaveRoom() skips leave() for a room that is no longer joined,
        // and then the tracks were never dropped.
        await APP.store.dispatch(destroyLocalTracks());

        // Leaving detached these from the conference, so this only stops capture and removes their effects.
        await Promise.allSettled(leftRoomTracks.filter(track => !track.disposed).map(track => track.dispose()));
    },

    /**
     * Disposes the previous conference's local media before an in-page rejoin, the way hangup() does.
     *
     * @returns {Promise<void>}
     */
    async _resetLocalMediaForRejoin() {
        await this._destroyLocalTracksOfLeftRoom();

        if (this._desktopAudioStream && !this._desktopAudioStream.disposed) {
            await this._desktopAudioStream.dispose();
        }
        this._desktopAudioStream = undefined;
        this._mixerEffect = undefined;

        // With the store emptied, startConference must take the new tracks' mute state from the user's intent
        // (base/media), not from the store - which would now report every device as muted.
        this._localTracksInitialized = false;
        this.localVideo = null;
        this.localAudio = null;
        this._untoggleScreenSharing = null;
        this.isSharingScreen && this._setSharingScreen(null);
        _replaceLocalAudioTrackQueue.clear();
        _replaceLocalVideoTrackQueue.clear();
    },

    /**
     * Used by the Breakout Rooms feature to join a breakout room or go back to the main room.
     */
    async joinRoom(roomName, options) {
        APP.UI.start();
        APP.store.dispatch(conferenceWillInit());

        // createInitialLocalTracks below opens a new camera and mic; dispose the previous room's first. leaveRoom()
        // dropped them from the store without disposing them, so they kept capturing - the old camera still running
        // its virtual background. Also reached from the [AutoReconnect] rejoin path.
        await this._resetLocalMediaForRejoin();

        // Restore initial state.
        this._localTracksInitialized = false;
        this.isSharingScreen = false;
        this.roomName = roomName;

        const { tryCreateLocalTracks, errors } = this.createInitialLocalTracks(options);
        const localTracks = await tryCreateLocalTracks;

        this._displayErrorsForCreateInitialLocalTracks(errors);
        localTracks.forEach(track => {
            if ((track.isAudioTrack() && this.isLocalAudioMuted())
                || (track.isVideoTrack() && this.isLocalVideoMuted())) {
                track.mute();
            }
        });

        this._createRoom(localTracks);

        // Read the passcode from preregData so we can join directly with the
        // password and avoid the CONFERENCE_FAILED → PASSWORD_REQUIRED cycle.
        const passcode = APP.store.getState()['features/base/mtApi']?.pregData?.passcode;

        return new Promise((resolve, reject) => {
            new ConferenceConnector(resolve, reject, this).connect(passcode || undefined);
        });
    },

    _createRoom(localTracks) {
        // Check if room already exists (e.g., during connection resume after internet disconnect)
        // If so, clean up the old room reference from emuc.rooms before creating a new one
        const roomName = APP.conference.roomName;
        const xmpp = APP.connection?.xmpp;

        if (xmpp && xmpp.isRoomCreated(roomName)) {
            logger.log('_createRoom: Room already exists in emuc.rooms, cleaning up old reference before creating new one');

            // Remove the old room reference from emuc.rooms to prevent
            // "conference with the same name has already been created" error
            const roomJid = xmpp.getRoomJid(roomName);

            xmpp.connection.emuc.doLeave(roomJid);
        }

        const conferenceOptions = this._getConferenceOptions();

        // Remember the media transport policy this room is actually built with. iceTransportPolicy is fixed
        // when the RTCPeerConnection is constructed, so this - and not the persisted "this network needs a
        // relay" flag - is what tells the ICE_FAILED handler whether a relay-only path has already been
        // tried. Recorded here rather than where the policy is computed because getConferenceOptions() is
        // also called after the room exists (the pre-call test, for one), and those calls must not be
        // mistaken for a new peer connection.
        setMediaRelayPolicyApplied(conferenceOptions.p2p?.iceTransportPolicy === 'relay'
            || Boolean(conferenceOptions.forceTurnRelay));

        room = APP.connection.initMHConference(roomName, conferenceOptions);

        APP.store.dispatch(conferenceWillJoin(room));

        // These references belong to the previous MHConference after a leave/rejoin. Keeping them here makes the
        // first mic/camera in the new room look like a replacement of a track that the new conference does not own;
        // the replacement then bypasses the normal add path and can leave the new sender unattached. The Redux tracks
        // passed below are the authoritative tracks for this room, so start with empty instance references.
        this.localAudio = null;
        this.localVideo = null;

        // Filter out the tracks that are muted.
        const tracks = localTracks.filter(track => !track.isMuted());

        // Muted tracks skip useVideoStream/useAudioStream here - the orphan re-attach on CONFERENCE_JOINED puts them on the
        // conference - but these references must still name them. muteVideo/muteAudio and device switches read a null
        // reference as "no camera/mic" and create a second one, which muteLocal() does alongside the redux unmute of the
        // first; replaceTrack(null, new) then took the first off the peerconnection while it was being unmuted. So a
        // camera turned on after joining - or re-joining a breakout room, or auto-reconnecting - with it off failed with
        // 'Track not found on the peerconnection', left that camera capturing, and failed virtual background on it.
        this.localVideo = localTracks.find(track => track.isVideoTrack() && track.videoType !== 'desktop'
            && track.isMuted()) ?? null;
        this.localAudio = localTracks.find(track => track.isAudioTrack() && track.isMuted()) ?? null;

        this._setLocalAudioVideoStreams(tracks);
        this._room = room; // FIXME do not use this

        sendLocalParticipant(APP.store, room);

        this._setupListeners();
    },

    /**
     * Sets local video and audio streams.
     *
     * @param {MHLocalTrack[]} tracks=[]
     * @returns {Promise[]}
     * @private
     */
    _setLocalAudioVideoStreams(tracks = []) {
        return tracks.map(track => {
            if (track.isAudioTrack()) {
                if (track) {
                    return this.useAudioStream(track);
                }
            } else if (track.isVideoTrack()) {
                logger.debug(
                    `_setLocalAudioVideoStreams is calling useVideoStream with track: ${track}`
                );
                if (track) {
                    return this.useVideoStream(track);
                }
            }

            logger.error('Ignored not an audio nor a video track: ', track);

            return Promise.resolve();
        });
    },

    _getConferenceOptions() {
        const options = getConferenceOptions(APP.store.getState(), APP.store.dispatch);

        options.createVADProcessor = createRnnoiseProcessor;

        return options;
    },

    /**
     * Start using provided video stream.
     * Stops previous video stream.
     * @param {MHLocalTrack} newTrack - new track to use or null
     * @param {Object} [options] - Options.
     * @param {MHLocalTrack} [options.replacing] - The camera this track was made to replace. When the camera has
     * changed by the time the request runs (the user switched device or turned it off meanwhile), the request is
     * obsolete: newTrack is disposed instead of undoing the user's change.
     * @returns {Promise}
     */
    useVideoStream(newTrack, { replacing } = {}) {
        logger.debug(`useVideoStream: ${newTrack}`);

        return new Promise((resolve, reject) => {
            _replaceLocalVideoTrackQueue.enqueue(onFinish => {
                const state = APP.store.getState();
                const dropIfSuperseded = currentTrack => {
                    if (!replacing || currentTrack === replacing) {
                        return false;
                    }
                    newTrack?.dispose().catch(() => undefined);
                    resolve();
                    onFinish();

                    return true;
                };

                // The camera is also replaced outside this queue (the virtual-background quality reopen, the
                // stuck-camera recreate), which disposes the track it replaces. A request queued for such a
                // track is obsolete by the time it runs: putting it on the conference can only fail, and the
                // failure path would then clear the live camera from the store.
                if (newTrack?.disposed) {
                    resolve();
                    onFinish();

                    return;
                }

                // When the prejoin page is displayed localVideo is not set
                // so just replace the video track from the store with the new one.
                if (isPrejoinPageVisible(state)) {
                    const oldTrack = getLocalMHVideoTrack(state);

                    if (dropIfSuperseded(oldTrack)) {
                        return;
                    }

                    logger.debug(
                        `useVideoStream on the prejoin screen: Replacing ${oldTrack} with ${newTrack}`
                    );

                    return APP.store
                        .dispatch(replaceLocalTrack(oldTrack, newTrack))
                        .then(resolve)
                        .catch(error => {
                            logger.error(
                                `useVideoStream failed on the prejoin screen: ${error}`
                            );
                            reject(error);
                        })
                        .then(onFinish);
                }

                // While a screenshare is active `this.localVideo` holds the DESKTOP track
                // (useScreenShareVideoStream assigns it, and presenter mode relies on that). Replacing it
                // would put the new camera on the screenshare's sender, so in that one case replace the
                // camera from the store and leave the field and the share flag alone. Everywhere else keep
                // using `this.localVideo`: joinRoom nulls it on purpose so the first camera takes the add
                // path, and the store already holds that same camera - resolving oldTrack from the store
                // there turned the add into replaceLocalTrack(camera, camera), which disposed the camera.
                const sharing = this.localVideo?.videoType === 'desktop';
                let oldTrack = sharing ? getLocalMHVideoTrack(state) : this.localVideo;

                // Those same outside replacements leave this.localVideo empty or pointing at the disposed
                // camera while the store holds the live one. Treating that as "no camera" turned the next
                // camera into an add next to the live one: the conference swapped them, but the store kept
                // both and the old camera was never released. The store camera is only taken when it is a
                // different track - the one joinRoom leaves unreferenced is the very track being added.
                if (newTrack && !sharing && (!oldTrack || oldTrack.disposed)) {
                    const storedCamera = getLocalMHVideoTrack(state);

                    if (storedCamera && storedCamera !== newTrack && !storedCamera.disposed) {
                        oldTrack = storedCamera;
                    }
                }

                if (dropIfSuperseded(oldTrack)) {
                    return;
                }

                logger.debug(
                    `useVideoStream: Replacing ${oldTrack} with ${newTrack}`
                );
                APP.store
                    .dispatch(
                        replaceLocalTrack(oldTrack, newTrack, room)
                    )
                    .then(() => {
                        if (!sharing) {
                            this.localVideo = newTrack;
                            this._setSharingScreen(newTrack);
                        }
                        if (newTrack) {
                            APP.UI.addLocalVideoStream(newTrack);
                        }
                        this.setVideoMuteStatus(this.isLocalVideoMuted());
                    })
                    .then(resolve)
                    .catch(error => {
                        logger.error(`useVideoStream failed: ${error}`);
                        reject(error);
                    })
                    .then(onFinish);
            });
        });
    },


    /**
     * Start using provided audio stream.
     * Stops previous audio stream.
     * @param {MHLocalTrack} newTrack - new track to use or null
     * @returns {Promise}
     */
    useAudioStream(newTrack) {
        return new Promise((resolve, reject) => {
            _replaceLocalAudioTrackQueue.enqueue(onFinish => {
                const state = APP.store.getState();

                // When the prejoin page is displayed localAudio is not set
                // so just replace the audio track from the store with the new one.
                if (isPrejoinPageVisible(state)) {
                    const oldTrack = getLocalMHAudioTrack(state);

                    return APP.store
                        .dispatch(replaceLocalTrack(oldTrack, newTrack))
                        .then(resolve)
                        .catch(reject)
                        .then(onFinish);
                }

                APP.store
                    .dispatch(
                        replaceLocalTrack(this.localAudio, newTrack, room)
                    )
                    .then(async () => {
                        this.localAudio = newTrack;
                        this.setAudioMuteStatus(this.isLocalAudioMuted());

                        if (newTrack) {
                            await this._maybeApplyAudioMixerEffect(newTrack);
                        }
                    })
                    .then(resolve)
                    .catch(reject)
                    .then(onFinish);
            });
        });
    },

    /**
     * Start using provided video stream.
     * Stops previous video stream.
     *
     * @param {MHLocalTrack} newTrack - New track to use or null.
     * @returns {Promise}
     */
    useScreenShareVideoStream(newTrack) {
        logger.debug(`useScreenShareVideoStream: ${newTrack}`, newTrack);

        return new Promise((resolve, reject) => {
            _replaceLocalVideoTrackQueue.enqueue(onFinish => {
                const state = APP.store.getState();
                const oldTrack = getLocalMHVideoTrack(state);
                const localScreenshare = getLocalDesktopTrack(state['features/base/tracks']);


                logger.debug(`useScreenShareVideoStream: Replacing ${oldTrack} with ${newTrack}`);

                // if (oldTrack === newTrack || (!oldTrack && !newTrack)) {
                //     resolve();
                //     onFinish();

                //     return;
                // }

                const trackAction = newTrack === null && this.isSharingScreen && localScreenshare
                    ? replaceLocalTrack(localScreenshare.mhTrack, null, room)
                    : this.isSharingScreen && localScreenshare
                        ? setScreenshareMuted(true)
                        : localScreenshare ? replaceLocalTrack(localScreenshare.mhTrack, newTrack, room) : addLocalTrack(newTrack);

                APP.store.dispatch(trackAction)
                    .then(() => {
                        // this.setVideoMuteStatus();
                        this.localVideo = newTrack;
                        this._setSharingScreen(newTrack);
                        if (newTrack) {
                            APP.UI.addLocalVideoStream(newTrack);
                        }
                        this.setVideoMuteStatus(this.isLocalVideoMuted());
                    })
                    .then(resolve)
                    .catch(error => {
                        logger.error(`useScreenShareVideoStream failed: ${error}`);
                        reject(error);
                    })
                    .then(onFinish);
            });
        });
    },

    /**
     * Sets `this.isSharingScreen` depending on provided video stream.
     * In case new screen sharing status is not equal previous one
     * it updates desktop sharing buttons in UI
     * and notifies external application.
     *
     * @param {MHLocalTrack} [newStream] - New stream to use or null.
     * @private
     * @returns {void}
     */
    _setSharingScreen(newStream) {
        const wasSharingScreen = this.isSharingScreen;

        this.isSharingScreen = newStream && newStream.videoType === 'desktop' && !newStream.muted;

        if (wasSharingScreen !== this.isSharingScreen) {
            const details = {};

            if (this.isSharingScreen) {
                details.sourceType = newStream.sourceType;
            }

            APP.API.notifyScreenSharingStatusChanged(
                this.isSharingScreen,
                details
            );
        }
    },

    /**
     * Start using provided audio stream.
     * Stops previous audio stream.
     * @param {MHLocalTrack} newTrack - new track to use or null
     * @returns {Promise}
     */
    // useAudioStream(newTrack) {
    //     return new Promise((resolve, reject) => {
    //         _replaceLocalAudioTrackQueue.enqueue(onFinish => {
    //             const state = APP.store.getState();

    //             // When the prejoin page is displayed localAudio is not set
    //             // so just replace the audio track from the store with the new one.
    //             if (isPrejoinPageVisible(state)) {
    //                 const oldTrack = getLocalMHAudioTrack(state);

    //                 return APP.store
    //                     .dispatch(replaceLocalTrack(oldTrack, newTrack))
    //                     .then(resolve)
    //                     .catch(reject)
    //                     .then(onFinish);
    //             }

    //             APP.store
    //                 .dispatch(
    //                     replaceLocalTrack(this.localAudio, newTrack, room)
    //                 )
    //                 .then(async () => {
    //                     this.localAudio = newTrack;
    //                     this.setAudioMuteStatus(this.isLocalAudioMuted());

    //                     // Apply AudioMixerEffect if needed
    //                     if (newTrack) {
    //                         await this._maybeApplyAudioMixerEffect(newTrack);
    //                     }
    //                 })
    //                 .then(resolve)
    //                 .catch(reject)
    //                 .then(onFinish);
    //         });
    //     });
    // },

    /**
     * Returns whether or not the conference is currently in audio only mode.
     *
     * @returns {boolean}
     */
    isAudioOnly() {
        return Boolean(APP.store.getState()['features/base/audio-only'].enabled);
    },

    videoSwitchInProgress: false,

    /**
     * This fields stores a handler which will create a Promise which turns off
     * the screen sharing and restores the previous video state (was there
     * any video, before switching to screen sharing ? Was it muted ?).
     *
     * Once called this fields is cleared to <tt>null</tt>.
     * @type {Function|null}
     */
    _untoggleScreenSharing: null,

    /**
     * Creates a Promise which turns off the screen sharing and restores
     * the previous state described by the arguments.
     *
     * This method is bound to the appropriate values, after switching to screen
     * sharing and stored in {@link _untoggleScreenSharing}.
     *
     * @param {boolean} didHaveVideo - Indicates if there was a camera video being
     * used, before switching to screen sharing.
     * @param {boolean} wasVideoMuted - Indicates if the video was muted, before
     * switching to screen sharing.
     * @returns {Promise} Resolved after the screen sharing is turned off, or
     * rejected with some error (no idea what kind of error, possible GUM error)
     * in case it fails.
     * @private
     */
    async _turnScreenSharingOff(didHaveVideo) {
        this._untoggleScreenSharing = null;
        this.videoSwitchInProgress = true;

        APP.store.dispatch(stopReceiver());

        const { partId } = APP.store.getState()['features/genericiframe'];
        const participants = APP.store.getState()['features/base/participants'];
        const participantsD = participants.filter(i => i.local === true);
        const stateMent
            = APP.store.getState()['features/genericiframe'].visible;
        const { editing, partId: participantID }
            = APP.store.getState()['features/etherpad'];

        if (stateMent && participantsD[0]?.id === partId) {
            APP.store.dispatch(sendWhiteBoardInstance(false));
        }
        if (editing && participantID === participantsD[0]?.id) {
            APP.store.dispatch(toogleEtherPad(false));
        }

        this._stopProxyConnection();
        if (config.enableScreenshotCapture) {
            APP.store.dispatch(toggleScreenshotCaptureEffect(false));
        }

        // It can happen that presenter GUM is in progress while screensharing is being turned off. Here it needs to
        // wait for that GUM to be resolved in order to prevent leaking the presenter track(this.localPresenterVideo
        // will be null when SS is being turned off, but it will initialize once GUM resolves).
        let promise = _prevMutePresenterVideo = _prevMutePresenterVideo.then(
            () => {
                // mute the presenter track if it exists.
                if (this.localPresenterVideo) {
                    APP.store.dispatch(
                        setVideoMuted(true, MEDIA_TYPE.PRESENTER)
                    );

                    return this.localPresenterVideo.dispose().then(() => {
                        APP.store.dispatch(
                            trackRemoved(this.localPresenterVideo)
                        );
                        this.localPresenterVideo = null;
                    });
                }
            }
        );

        // If system audio was also shared stop the AudioMixerEffect and dispose of the desktop audio track.
        if (this._mixerEffect) {
            const localAudio = getLocalMHAudioTrack(APP.store.getState());

            if (localAudio) {
                await localAudio.setEffect(undefined);
            }
            await this._desktopAudioStream.dispose();
            this._mixerEffect = undefined;
            this._desktopAudioStream = undefined;

        // In case there was no local audio when screen sharing was started the fact that we set the audio stream to
        // null will take care of the desktop audio stream cleanup.
        } else if (this._desktopAudioStream) {
            await room.replaceTrack(this._desktopAudioStream, null);
            this._desktopAudioStream.dispose();
            this._desktopAudioStream = undefined;
        }

        // Update screen audio share state
        // APP.store.dispatch(setScreenAudioShareState(false));

        // Helper function to check if video devices are available
        const areVideoDevicesAvailable = () => {
            const state = APP.store.getState();
            const devices = state['features/base/devices'];

            // Check if video input devices are available
            return devices
                && devices.availableDevices
                && devices.availableDevices.videoInput
                && devices.availableDevices.videoInput.length > 0;
        };

        // Helper function to check if video is actually available
        const isVideoActuallyAvailable = () => {
            const state = APP.store.getState();
            const tracks = state['features/base/tracks'];
            const localVideoTrack = getLocalVideoTrack(tracks);

            // Check if there's a valid local video track that's not a desktop track
            return localVideoTrack
                && localVideoTrack.mhTrack
                && localVideoTrack.videoType !== 'desktop'
                && !localVideoTrack.muted;
        };

        // Returns the camera track to fall back to once the screenshare is muted.
        //
        // The screenshare is its own source here, so the camera is never taken off the conference while sharing -
        // isVideoActuallyAvailable() is only true when that live, unmuted camera track is still in the store. This
        // used to open a *second* camera with createLocalTracksF() at that point, and useScreenShareVideoStream()
        // below only mutes the screenshare with it: the new track was never added to the conference or the store and
        // never disposed. Every stop of a screenshare leaked one more running camera capture (extra CPU for the rest
        // of the call), attached it to the local preview in place of the camera actually being sent, and left
        // this.localVideo pointing at a track the conference does not know, so later camera switches replaced a
        // track that was not there.
        const safelyCreateVideoTrack = async () => {
            if (!isVideoActuallyAvailable()) {
                logger.debug('No live camera track to restore after screen sharing.');

                return null;
            }

            return getLocalMHVideoTrack(APP.store.getState()) ?? null;
        };

        // Helper function to safely use video stream
        const safelyUseVideoStream = async stream => {
            // if (stream) {
            try {

                await this.useScreenShareVideoStream(stream);

                logger.debug('Successfully switched to video stream');
            } catch (error) {
                logger.warn('Failed to use video stream, continuing without video:', error);
            }

            // }
        };

        // Determine if we should attempt to restore video based on actual video availability and device availability
        const shouldRestoreVideo = didHaveVideo
            && (isVideoActuallyAvailable() || areVideoDevicesAvailable());

        if (shouldRestoreVideo) {
            logger.debug('Attempting to restore video after screen sharing');
            promise = promise
                .then(() => safelyCreateVideoTrack())
                .then(stream => safelyUseVideoStream(stream))
                .catch(error => {
                    logger.error('Error in video track creation/usage, continuing:', error);

                    // Don't reject the promise, just log the error and continue
                });
        } else {
            logger.debug('Skipping video restoration - no video track available and no video devices detected');
            const localVideoTrackDesktop = getLocalVideoTrackDesktop(APP.store.getState());

            if (localVideoTrackDesktop) {
                try {
                    APP.store.dispatch(
                        trackMutedChanged(localVideoTrackDesktop.mhTrack, true)
                    );

                    room.sendCommand('desktopTrackMuted', {
                        attributes: {
                            id: localVideoTrackDesktop.participantId,
                            muted: true,
                            videoType: 'desktop'
                        }
                    });
                } catch (error) {
                    logger.warn('Failed to mute desktop track:', error);
                }
            }

            promise = promise
                .then(() => safelyCreateVideoTrack())
                .then(stream => safelyUseVideoStream(stream))
                .then(() => {
                    try {
                        muteLocalVideo(true);
                        logger.debug('Successfully muted local video');
                    } catch (error) {
                        logger.warn('Failed to mute local video:', error);
                    }
                })
                .catch(error => {
                    logger.error('Error in video handling, continuing:', error);

                    // Don't reject the promise, just log the error and continue
                });
        }

        return promise.then(
            () => {
                this.videoSwitchInProgress = false;
                sendAnalytics(createScreenSharingEvent('stopped'));
                logger.debug('Screen sharing stopped successfully.');
            },
            error => {
                this.videoSwitchInProgress = false;
                logger.error(`_turnScreenSharingOff failed: ${error}`);

                // Even if there was an error, we should still mark screen sharing as stopped
                // to prevent the UI from being stuck in a screensharing state
                sendAnalytics(createScreenSharingEvent('stopped'));
                logger.debug('Screen sharing stopped with errors.');

                // Don't re-throw the error to prevent the UI from being stuck
            }
        );
    },

    /**
     * Toggles between screen sharing and camera video if the toggle parameter
     * is not specified and starts the procedure for obtaining new screen
     * sharing/video track otherwise.
     *
     * @param {boolean} [toggle] - If true - new screen sharing track will be
     * obtained. If false - new video track will be obtain. If not specified -
     * toggles between screen sharing and camera video.
     * @param {Object} [options] - Screen sharing options that will be passed to
     * createLocalTracks.
     * @param {Array<string>} [options.desktopSharingSources] - Array with the
     * sources that have to be displayed in the desktop picker window ('screen',
     * 'window', etc.).
     * @returns {Promise.<T>}
     */
    async toggleScreenSharing(
            toggle = !this._untoggleScreenSharing,
            options = {}
    ) {
        logger.debug(`toggleScreenSharing: ${toggle}`);
        if (this.videoSwitchInProgress) {
            return Promise.reject(i18next.t('dialog.switchInProgress'));
        }
        if (!MeetHourJS.isDesktopSharingEnabled()) {
            return Promise.reject(
                i18next.t('dialog.cannotToggleScreenSharingNotSupported')
            );
        }

        if (this.isAudioOnly()) {
            return Promise.reject(APP.store.dispatch(
                showNotification({
                    descriptionKey: 'notify.startSilentDescription',
                    titleKey: 'dialog.noScreensharingInAudioOnlyMode'
                })
            ));

        }

        if (toggle) {
            try {
                await this._switchToScreenSharing(options);

                return;
            } catch (err) {
                // `state` was never declared in this scope (it exists as a local in
                // `_turnScreenSharingOff` above, not here) - every reference to it below threw
                // `ReferenceError: state is not defined` and replaced `err` (which could be the
                // real cause, e.g. a WebRTC "no transceiver" replaceTrack failure) with that
                // ReferenceError before anything reached the `logger.error` below, let alone the
                // whiteboard/etherpad cleanup this block exists to do. Since `toggleScreenSharing`
                // is async and nothing wraps this call, the ReferenceError became this function's
                // unhandled rejection - masking the original error's message and stack, and
                // skipping the cleanup dispatches entirely on every screenshare-start failure.
                try {
                    const state = APP.store.getState();
                    const { partId } = state['features/genericiframe'];
                    const participants = state['features/base/participants'];
                    const participantsD = participants.filter(
                        i => i.local === true
                    );
                    const stateMent = state['features/genericiframe'].visible;
                    const { editing, partId: participantID } = state['features/etherpad'];

                    if (stateMent && participantsD[0]?.id === partId) {
                        APP.store.dispatch(sendWhiteBoardInstance(false));
                    }
                    if (editing && participantID === participantsD[0]?.id) {
                        APP.store.dispatch(toogleEtherPad(false));
                    }
                    room.sendCommand('WHITEBOARD_COMMAND', {
                        attributes: { value: 'false',
                            partId: null }
                    });
                    room.sendCommand('ETHERPAD_COMMIT', {
                        attributes: { value: 'false',
                            partId: null }
                    });
                } catch (cleanupError) {
                    // The whiteboard/etherpad cleanup above is best-effort. Letting a failure in it
                    // escape here would repeat exactly the bug just described - masking the real
                    // screensharing error with an unrelated one and turning a recoverable failure
                    // into an unhandled rejection.
                    logger.error('Failed to clean up whiteboard/etherpad state after a failed'
                        + ' screensharing switch', cleanupError);
                }

                logger.error('Failed to switch to screensharing', err);

                return;
            }
        }

        return this._untoggleScreenSharing
            ? this._untoggleScreenSharing()
            : Promise.resolve();
    },

    /** .........
     * Creates desktop (screensharing) {@link MHLocalTrack}
     *
     * @param {Object} [options] - Screen sharing options that will be passed to
     * createLocalTracks.
     * @param {Object} [options.desktopSharing]
     * @param {Object} [options.desktopStream] - An existing desktop stream to
     * use instead of creating a new desktop stream.
     * @returns {Promise.<MHLocalTrack>} - A Promise resolved with
     * {@link MHLocalTrack} for the screensharing or rejected with
     * {@link MHTrackError}.
     *
     * @private
     */
    _createDesktopTrack(options = {}) {
        const didHaveVideo = !this.isLocalVideoMuted();

        const getDesktopStreamPromise = options.desktopStream
            ? Promise.resolve([ options.desktopStream ])
            : createLocalTracksF({
                desktopSharingSourceDevice: options.desktopSharingSources
                    ? null : config._desktopSharingSourceDevice,
                desktopSharingSources: options.desktopSharingSources,
                devices: [ 'desktop' ]
            });

        return getDesktopStreamPromise.then(desktopStreams => {
            // Stores the "untoggle" handler which remembers whether was
            // there any video before and whether was it muted.
            this._untoggleScreenSharing
                = this._turnScreenSharingOff.bind(this, didHaveVideo);

            const desktopVideoStream = desktopStreams.find(stream => stream.getType() === MEDIA_TYPE.VIDEO);
            const desktopAudioStream = desktopStreams.find(stream => stream.getType() === MEDIA_TYPE.AUDIO);

            if (desktopAudioStream) {
                desktopAudioStream.on(
                    MHTrackEvents.LOCAL_TRACK_STOPPED,
                    () => {
                        logger.debug(`Local screensharing audio track stopped. ${this.isSharingScreen}`);

                        // Handle case where screen share was stopped from  the browsers 'screen share in progress'
                        // window. If audio screen sharing is stopped via the normal UX flow this point shouldn't
                        // be reached.
                        isScreenAudioShared(APP.store.getState())
                            && this._untoggleScreenSharing
                            && this._untoggleScreenSharing();
                    }
                );
            }

            if (desktopVideoStream) {
                desktopVideoStream.on(
                    MHTrackEvents.LOCAL_TRACK_STOPPED,
                    () => {
                        logger.debug(`Local screensharing track stopped. ${this.isSharingScreen}`);

                        // If the stream was stopped during screen sharing
                        // session then we should switch back to video.
                        this.isSharingScreen
                            && this._untoggleScreenSharing
                            && this._untoggleScreenSharing();
                    }
                );
            }

            return desktopStreams;
        }, error => {
            throw error;
        });
    },

    /**
     * Validates screen sharing capabilities and permissions.
     *
     * @returns {boolean} - True if screen sharing is supported and available.
     * @private
     */
    _validateScreenSharingCapabilities() {
        // Check if screen sharing is supported by the browser
        if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
            logger.warn('Screen sharing not supported by browser');

            return false;
        }

        // Check if we're in a secure context (required for screen sharing)
        if (!window.isSecureContext) {
            logger.warn('Screen sharing requires secure context (HTTPS)');

            return false;
        }

        // Check if screen sharing is enabled in config
        if (config.disableScreenSharing) {
            logger.warn('Screen sharing disabled in configuration');

            return false;
        }

        return true;
    },

    /**
     * Checks if screen sharing is currently active.
     *
     * @returns {boolean} - True if screen sharing is currently active.
     */
    isScreenSharingActive() {
        return this.isSharingScreen;
    },

    /**
     * Gets the current screen sharing status and details.
     *
     * @returns {Object} - Object containing screen sharing status and details.
     */
    getScreenSharingStatus() {
        return {
            isActive: this.isSharingScreen,
            hasAudio: Boolean(this._desktopAudioStream),
            hasVideo: Boolean(this.localVideo && this.localVideo.videoType === 'desktop'),
            sourceType: this.localVideo?.sourceType,
            canShare: this._validateScreenSharingCapabilities()
        };
    },

    /**
     * Requests screen sharing permission and starts screen sharing if granted.
     *
     * @param {Object} options - Screen sharing options.
     * @returns {Promise} - Promise that resolves when screen sharing starts or rejects on error.
     */
    async requestScreenSharingPermission(options = {}) {
        if (!this._validateScreenSharingCapabilities()) {
            throw new Error('Screen sharing not supported or not available');
        }

        if (this.isSharingScreen) {
            logger.warn('Screen sharing is already active');

            return Promise.resolve();
        }

        try {
            // Request permission first
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: options.includeAudio || false
            });

            // If permission granted, start screen sharing
            return this.toggleScreenSharing(true, {
                ...options,
                desktopStream: stream
            });
        } catch (error) {
            logger.error('Screen sharing permission denied or failed:', error);
            throw error;
        }
    },

    /**
     * Creates a new instance of presenter effect. A new video track is created
     * using the new set of constraints that are calculated based on
     * the height of the desktop that is being currently shared.
     *
     * @param {number} height - The height of the desktop stream that is being
     * currently shared.
     * @param {string} cameraDeviceId - The device id of the camera to be used.
     * @returns {Promise<MHStreamPresenterEffect>} - A promise resolved with
     * {@link MHStreamPresenterEffect} if it succeeds.
     */
    async _createPresenterStreamEffect(height = null, cameraDeviceId = null) {
        if (!this.localPresenterVideo) {
            const camera
                = cameraDeviceId
                ?? getUserSelectedCameraDeviceId(APP.store.getState());

            try {
                this.localPresenterVideo = await createLocalPresenterTrack(
                    { cameraDeviceId: camera },
                    height
                );
            } catch (err) {
                logger.error(
                    'Failed to create a camera track for presenter',
                    err
                );

                return;
            }
            APP.store.dispatch(trackAdded(this.localPresenterVideo));
        }
        try {
            const effect = await createPresenterEffect(
                this.localPresenterVideo.stream
            );

            return effect;
        } catch (err) {
            logger.error('Failed to create the presenter effect', err);
        }
    },

    /**
     * Tries to turn the presenter video track on or off. If a presenter track
     * doesn't exist, a new video track is created.
     *
     * @param mute - True for mute and false for unmute.
     *
     * @private
     */
    async _mutePresenterVideo(mute) {
        const maybeShowErrorDialog = error => {
            APP.store.dispatch(notifyCameraError(error));
        };

        // Check for NO-OP
        if (
            mute
            && (!this.localPresenterVideo || this.localPresenterVideo.isMuted())
        ) {
            return;
        } else if (
            !mute
            && this.localPresenterVideo
            && !this.localPresenterVideo.isMuted()
        ) {
            return;
        }

        // Create a new presenter track and apply the presenter effect.
        if (!this.localPresenterVideo && !mute) {
            const { height, width }
                = this.localVideo.track.getSettings()
                ?? this.localVideo.track.getConstraints();
            const isPortrait = height >= width;
            const DESKTOP_STREAM_CAP = 720;

            // Config.js setting for resizing high resolution desktop tracks to 720p when presenter is turned on.
            const resizeEnabled
                = config.videoQuality
                && config.videoQuality.resizeDesktopForPresenter;
            const highResolutionTrack
                = (isPortrait && width > DESKTOP_STREAM_CAP)
                || (!isPortrait && height > DESKTOP_STREAM_CAP);

            // Resizing the desktop track for presenter is causing blurriness of the desktop share on chrome.
            // Disable resizing by default, enable it only when config.js setting is enabled.
            // Firefox doesn't return width and height for desktop tracks. Therefore, track needs to be resized
            // for creating the canvas for presenter.
            const resizeDesktopStream
                = browser.isFirefox() || (highResolutionTrack && resizeEnabled);

            if (resizeDesktopStream) {
                let desktopResizeConstraints = {};

                if (height && width) {
                    const advancedConstraints = [
                        { aspectRatio: (width / height).toPrecision(4) }
                    ];
                    const constraint = isPortrait
                        ? { width: DESKTOP_STREAM_CAP }
                        : { height: DESKTOP_STREAM_CAP };

                    advancedConstraints.push(constraint);
                    desktopResizeConstraints.advanced = advancedConstraints;
                } else {
                    desktopResizeConstraints = {
                        width: 1280,
                        height: 720
                    };
                }

                // Apply the contraints on the desktop track.
                try {
                    await this.localVideo.track.applyConstraints(
                        desktopResizeConstraints
                    );
                } catch (err) {
                    logger.error(
                        'Failed to apply constraints on the desktop stream for presenter mode',
                        err
                    );

                    return;
                }
            }
            const trackHeight = resizeDesktopStream
                ? this.localVideo.track.getSettings().height
                  ?? DESKTOP_STREAM_CAP
                : height;
            let effect;

            try {
                effect = await this._createPresenterStreamEffect(trackHeight);
            } catch (err) {
                logger.error('Failed to unmute Presenter Video', err);
                maybeShowErrorDialog(err);

                return;
            }

            // Replace the desktop track on the peerconnection.
            try {
                await this.localVideo.setEffect(effect);
                APP.store.dispatch(setVideoMuted(mute, MEDIA_TYPE.PRESENTER));
                this.setVideoMuteStatus(mute);
            } catch (err) {
                logger.error('Failed to apply the Presenter effect', err);
            }
        } else {
            APP.store.dispatch(setVideoMuted(mute, MEDIA_TYPE.PRESENTER));
        }
    },

    /**
     * Tries to switch to the screensharing mode by disposing camera stream and
     * replacing it with a desktop one.
     *
     * @param {Object} [options] - Screen sharing options that will be passed to
     * createLocalTracks.
     *
     * @returns {Promise} - A Promise resolved if the operation succeeds or
     * rejected with some unknown type of error in case it fails. Promise will
     * be rejected immediately if {@link videoSwitchInProgress} is true.
     *
     * @private
     */
    _switchToScreenSharing(options = {}) {
        if (this.videoSwitchInProgress) {
            return Promise.reject(i18next.t('dialog.switchInProgress'));
        }

        this.videoSwitchInProgress = true;

        return this._createDesktopTrack(options)
            .then(async streams => {
                const desktopVideoStream = streams.find(
                    stream => stream.getType() === MEDIA_TYPE.VIDEO
                );

                if (desktopVideoStream) {
                    logger.debug(
                        `_switchToScreenSharing is using ${desktopVideoStream} for useVideoStream`
                    );
                    await this.useScreenShareVideoStream(desktopVideoStream);
                }

                this._desktopAudioStream = streams.find(
                    stream => stream.getType() === MEDIA_TYPE.AUDIO
                );

                if (this._desktopAudioStream) {
                    // If there is a localAudio stream, mix in the desktop audio stream captured by the screen sharing
                    // api.
                    if (this.localAudio) {
                        this._mixerEffect = new AudioMixerEffect(
                            this._desktopAudioStream
                        );

                        await this.localAudio.setEffect(this._mixerEffect);
                    } else {
                        // If no local stream is present ( i.e. no input audio devices) we use the screen share audio
                        // stream as we would use a regular stream.
                        // eslint-disable-next-line no-lonely-if
                        if (this._desktopAudioStream) {
                            await this.useAudioStream(this._desktopAudioStream);
                        }
                    }

                    // Update screen audio share state
                    // APP.store.dispatch(setScreenAudioShareState(true));
                }
            })
            .then(() => {
                this.videoSwitchInProgress = false;
                if (config.enableScreenshotCapture) {
                    APP.store.dispatch(toggleScreenshotCaptureEffect(true));
                }
                sendAnalytics(createScreenSharingEvent('started'));
                logger.log('Screen sharing started');
            })
            .catch(error => {
                this.videoSwitchInProgress = false;

                // Pawel: With this call I'm trying to preserve the original
                // behaviour although it is not clear why would we "untoggle"
                // on failure. I suppose it was to restore video in case there
                // was some problem during "this.useVideoStream(desktopStream)".
                // It's important to note that the handler will not be available
                // if we fail early on trying to get desktop media (which makes
                // sense, because the camera video is still being used, so
                // nothing to "untoggle").
                if (this._untoggleScreenSharing) {
                    this._untoggleScreenSharing();
                }

                // FIXME the code inside of _handleScreenSharingError is
                // asynchronous, but does not return a Promise and is not part
                // of the current Promise chain.
                this._handleScreenSharingError(error);

                return Promise.reject(error);
            });
    },

    /**
     * Handles {@link MHTrackError} returned by the lib-meet-hour when
     * trying to create screensharing track. It will either do nothing if
     * the dialog was canceled on user's request or display an error if
     * screensharing couldn't be started.
     *
     * @param {MHTrackError} error - The error returned by
     * {@link _createDesktopTrack} Promise.
     * @private
     */
    _handleScreenSharingError(error) {
        if (error.name === MHTrackErrors.SCREENSHARING_USER_CANCELED) {
            return;
        }

        logger.error('failed to share local desktop', error);

        // Reset screen sharing state
        this.isSharingScreen = false;
        this._untoggleScreenSharing = null;
        this.videoSwitchInProgress = false;

        // Clean up any existing desktop audio stream
        if (this._desktopAudioStream) {
            this._desktopAudioStream.dispose();
            this._desktopAudioStream = undefined;
        }

        // Clean up mixer effect
        if (this._mixerEffect) {
            this._mixerEffect = undefined;
        }

        // Handling:
        // MHTrackErrors.CONSTRAINT_FAILED
        // MHTrackErrors.PERMISSION_DENIED
        // MHTrackErrors.SCREENSHARING_GENERIC_ERROR
        // and any other
        let descriptionKey;
        let titleKey;

        if (error.name === MHTrackErrors.PERMISSION_DENIED) {
            descriptionKey = 'dialog.screenSharingPermissionDeniedError';
            titleKey = 'dialog.screenSharingFailedTitle';
        } else if (error.name === MHTrackErrors.CONSTRAINT_FAILED) {
            descriptionKey = 'dialog.cameraConstraintFailedError';
            titleKey = 'deviceError.cameraError';
        } else if (error.name === MHTrackErrors.SCREENSHARING_GENERIC_ERROR) {
            descriptionKey = 'dialog.screenSharingFailed';
            titleKey = 'dialog.screenSharingFailedTitle';
        } else if (error === AUDIO_ONLY_SCREEN_SHARE_NO_TRACK) {
            descriptionKey = 'notify.screenShareNoAudio';
            titleKey = 'notify.screenShareNoAudioTitle';
        }

        APP.store.dispatch(showErrorNotification({
            descriptionKey,
            titleKey
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    },

    /**
     * Removes the APP.UI listeners registered by {@link _setupListeners}.
     *
     * @private
     * @returns {void}
     */
    _removeUIListeners() {
        for (const [ type, listener ] of this._uiListeners || []) {
            APP.UI.removeListener(type, listener);
        }
        this._uiListeners = [];
    },

    /**
     * Setup interaction between conference and UI.
     */
    _setupListeners() {
        // This runs for every conference created in the page (breakout rooms, in-page reconnects) while APP.UI lives
        // for the whole page, so the previous conference's handlers must go first or each UI event (a camera switch,
        // a mute) is handled once per past conference.
        this._removeUIListeners();

        const addUIListener = (type, listener) => {
            APP.UI.addListener(type, listener);
            this._uiListeners.push([ type, listener ]);
        };

        // add local streams when joined to the conference
        room.on(MHConferenceEvents.CONFERENCE_JOINED, () => {
            this._onConferenceJoined();
        });

        room.on(
            MHConferenceEvents.CONFERENCE_JOIN_IN_PROGRESS,
            () => APP.store.dispatch(conferenceJoinInProgress(room)));

        room.on(
            MHConferenceEvents.CONFERENCE_LEFT,
            (...args) => {
                APP.store.dispatch(conferenceTimestampChanged(0));
                APP.store.dispatch(conferenceLeft(room, ...args));
            });

        room.on(MHConferenceEvents.CONFERENCE_UNIQUE_ID_SET, (...args) =>
            APP.store.dispatch(conferenceUniqueIdSet(room, ...args))
        );

        room.on(
            MHConferenceEvents.AUTH_STATUS_CHANGED,
            (authEnabled, authLogin) =>
                APP.store.dispatch(authStatusChanged(authEnabled, authLogin))
        );

        room.on(MHConferenceEvents.PARTCIPANT_FEATURES_CHANGED, user => {
            APP.store.dispatch(updateRemoteParticipantFeatures(user));
        });
        room.on(MHConferenceEvents.USER_JOINED, (id, user) => {
            // The logic shared between RN and web.
            commonUserJoinedHandling(APP.store, room, user);

            if (user.isHidden()) {
                return;
            }

            APP.store.dispatch(updateRemoteParticipantFeatures(user));
            logger.log(`USER ${id} connnected:`, user);
            APP.UI.addUser(user);
        });

        room.on(MHConferenceEvents.USER_LEFT, (id, user) => {
            // The logic shared between RN and web.
            commonUserLeftHandling(APP.store, room, user);

            if (user.isHidden()) {
                return;
            }

            logger.log(`USER ${id} LEFT:`, user);

            APP.UI.onSharedVideoStop(id);
        });

        room.on(MHConferenceEvents.USER_STATUS_CHANGED, (id, status) => {
            APP.store.dispatch(participantPresenceChanged(id, status));

            const user = room.getParticipantById(id);

            if (user) {
                APP.UI.updateUserStatus(user, status);
            }
        });

        room.on(MHConferenceEvents.USER_ROLE_CHANGED, (id, role) => {
            if (this.isLocalId(id)) {
                //  logger.info(`My role changed, new role: ${role}`);

                APP.store.dispatch(localParticipantRoleChanged(role));
                APP.API.notifyUserRoleChanged(id, role);
            } else {
                APP.store.dispatch(participantRoleChanged(id, role));
            }
        });

        room.on(MHConferenceEvents.TRACK_ADDED, track => {
            if (!track) {
                return;
            }

            APP.store.dispatch(trackAdded(track));
        });

        room.on(MHConferenceEvents.TRACK_REMOVED, track => {

            if (!track) {
                return;
            }

            APP.store.dispatch(trackRemoved(track));
        });

        room.on(MHConferenceEvents.TRACK_AUDIO_LEVEL_CHANGED, (id, lvl) => {
            let newLvl = lvl;

            if (
                this.isLocalId(id)
                && this.localAudio
                && this.localAudio.isMuted()
            ) {
                newLvl = 0;
            }

            if (config.debug) {
                this.audioLevelsMap[id] = newLvl;
                if (config.debugAudioLevels) {
                    logger.log(`AudioLevel:${id}/${newLvl}`);
                }
            }

            APP.UI.setAudioLevel(id, newLvl);
        });

        room.on(
            MHConferenceEvents.TRACK_MUTE_CHANGED,
            (track, participantThatMutedUs) => {
                if (participantThatMutedUs) {
                    APP.store.dispatch(
                        participantMutedUs(
                            participantThatMutedUs,
                            track
                        )
                    );
                    logger.log('TRACK_MUTE_CHANGED while screen sharing');
                    if (this.isSharingScreen && track.isVideoTrack()) {
                        logger.log('TRACK_MUTE_CHANGED while screen sharing');
                        this._turnScreenSharingOff(false);
                    }
                }
            }
        );

        room.on(MHConferenceEvents.SUBJECT_CHANGED, subject =>
            APP.store.dispatch(conferenceSubjectChanged(subject))
        );

        room.addCommandListener(
            this.commands.defaults.CONFERENCE_SUBJECT,
            ({ value }) => {
                APP.store.dispatch(conferenceSubjectChanged(value));
            }
        );

        room.on(
            MHConferenceEvents.LAST_N_ENDPOINTS_CHANGED,
            (leavingIds, enteringIds) =>
                APP.UI.handleLastNEndpoints(leavingIds, enteringIds)
        );

        room.on(MHConferenceEvents.P2P_STATUS, (mhConference, p2p) =>
            APP.store.dispatch(p2pStatusChanged(p2p))
        );

        room.on(
            MHConferenceEvents.PARTICIPANT_CONN_STATUS_CHANGED,
            (id, connectionStatus) =>
                APP.store.dispatch(
                    participantConnectionStatusChanged(id, connectionStatus)
                )
        );

        room.on(MHConferenceEvents.DOMINANT_SPEAKER_CHANGED, id =>
            APP.store.dispatch(dominantSpeakerChanged(id, room))
        );

        room.on(
            MHConferenceEvents.CONFERENCE_CREATED_TIMESTAMP,
            conferenceTimestamp => {
                APP.store.dispatch(
                    conferenceTimestampChanged(conferenceTimestamp)
                );
                APP.API.notifyConferenceCreatedTimestamp(conferenceTimestamp);
            }

        );

        room.on(MHConferenceEvents.CONNECTION_INTERRUPTED, () => {
            APP.store.dispatch(
                localParticipantConnectionStatusChanged(
                    MHParticipantConnectionStatus.INTERRUPTED
                )
            );
        });

        room.on(MHConferenceEvents.CONNECTION_RESTORED, () => {
            APP.store.dispatch(
                localParticipantConnectionStatusChanged(
                    MHParticipantConnectionStatus.ACTIVE
                )
            );
        });

        room.on(MHConferenceEvents.DISPLAY_NAME_CHANGED, (id, displayName) => {
            const formattedDisplayName = getNormalizedDisplayName(displayName);

            APP.store.dispatch(
                participantUpdated({
                    conference: room,
                    id,
                    name: formattedDisplayName
                })
            );
            APP.API.notifyDisplayNameChanged(id, {
                displayName: formattedDisplayName,
                formattedDisplayName: appendSuffix(
                    formattedDisplayName
                        || interfaceConfig.DEFAULT_REMOTE_DISPLAY_NAME
                )
            });
        });
        room.on(MHConferenceEvents.BOT_TYPE_CHANGED, (id, botType) => {
            APP.store.dispatch(
                participantUpdated({
                    conference: room,
                    id,
                    botType
                })
            );
        });

        // isTranscribingStatus adding extra condition that when transcription is on,letting transcriber not to leave the call,when stopping the recorder
        room.on(
            MHConferenceEvents.TRANSCRIPTION_STATUS_CHANGED,
            (status, id, abruptly) => {
                const isTranscribingStatus = APP.store.getState()['features/transcribing'].isTranscribing;

                if (status === MeetHourJS.constants.transcriptionStatus.ON) {
                    APP.store.dispatch(transcriberJoined(id));
                } else if (status === MeetHourJS.constants.transcriptionStatus.OFF && isTranscribingStatus === false) {
                    APP.store.dispatch(transcriberLeft(id, abruptly));
                }
            });

        room.on(MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, (...args) => {
            APP.store.dispatch(endpointMessageReceived(...args));
            if (args && args.length >= 2) {
                const [ sender, eventData ] = args;

                if (eventData.name === ENDPOINT_TEXT_MESSAGE_NAME) {
                    APP.API.notifyEndpointTextMessageReceived({
                        senderInfo: {
                            jid: sender._jid,
                            id: sender._id
                        },
                        eventData
                    });
                }
            }
        });

        room.on(
            MHConferenceEvents.NON_PARTICIPANT_MESSAGE_RECEIVED,
            (...args) => {
                APP.store.dispatch(nonParticipantMessageReceived(...args));
                APP.API.notifyNonParticipantMessageReceived(...args);
            });

        room.on(
            MHConferenceEvents.LOCK_STATE_CHANGED,
            (...args) => APP.store.dispatch(lockStateChanged(room, ...args)));

        room.on(MHConferenceEvents.KICKED, (participant, reason) => {
            this.autoReconnectSuppressed = true;
            this.isReconnecting = false;
            _clearAutoReconnectLifecycleState();
            APP.store.dispatch(kickedOut(room, participant, reason));
        });

        room.on(MHConferenceEvents.PARTICIPANT_KICKED, (kicker, kicked) => {
            APP.store.dispatch(participantKicked(kicker, kicked));
        });

        room.on(MHConferenceEvents.SUSPEND_DETECTED, () => {
            APP.store.dispatch(suspendDetected());
        });

        addUIListener(UIEvents.AUDIO_MUTED, muted => {
            this.muteAudio(muted);
        });
        addUIListener(UIEvents.VIDEO_MUTED, muted => {
            this.muteVideo(muted);
        });

        room.addCommandListener(
            this.commands.defaults.ETHERPAD,
            ({ value }) => {
                APP.UI.initEtherpad(value);

                // TODO: Remove as soon as the one below works
                APP.UI.initGenericIFrame();
            }
        );

        room.addCommandListener(
            'desktopTrackMuted',
            value => {
                const { id, muted, videoType } = value.attributes;
                const desktopTrack = APP.store.getState()['features/base/tracks'].find(track => track.participantId === id && track.videoType === videoType);

                if (desktopTrack && !desktopTrack.local) {
                    APP.store.dispatch(trackMutedChanged(desktopTrack.mhTrack, muted === 'true'));

                    if (muted === 'true') {
                        APP.store.dispatch(toggleTileView(true));
                    } else {
                        APP.store.dispatch(selectParticipantInLargeVideo(id));

                        if (APP.store.getState()['features/video-layout'].tileViewEnabled) {
                            APP.store.dispatch(toggleTileView(false));
                        }
                    }
                }
            }
        );

        room.addCommandListener(this.commands.defaults.WHITEBOARD, () => {
            APP.UI.initGenericIFrame();
        });

        addUIListener(
            UIEvents.EMAIL_CHANGED,
            this.changeLocalEmail.bind(this)
        );
        room.addCommandListener(this.commands.defaults.EMAIL, (data, from) => {
            APP.store.dispatch(
                participantUpdated({
                    conference: room,
                    id: from,
                    email: data.value
                })
            );
        });

        room.addCommandListener(
            this.commands.defaults.AVATAR_URL,
            (data, from) => {
                APP.store.dispatch(
                    participantUpdated({
                        conference: room,
                        id: from,
                        avatarURL: data.value
                    })
                );
            }
        );

        addUIListener(
            UIEvents.NICKNAME_CHANGED,
            this.changeLocalDisplayName.bind(this)
        );

        room.on(
            MHConferenceEvents.START_MUTED_POLICY_CHANGED,
            ({ audio, video }) => {
                APP.store.dispatch(onStartMutedPolicyChanged(audio, video));
            }
        );
        room.on(MHConferenceEvents.STARTED_MUTED, () => {
            const audioMuted = room.isStartAudioMuted();
            const videoMuted = room.isStartVideoMuted();
            const localTracks = getLocalTracks(
                APP.store.getState()['features/base/tracks']
            );
            const promises = [];

            APP.store.dispatch(setAudioMuted(audioMuted));
            APP.store.dispatch(setVideoMuted(videoMuted));

            // Remove the tracks from the peerconnection.
            for (const track of localTracks) {
                if (audioMuted && track?.mhTrack?.getType() === MEDIA_TYPE.AUDIO) {
                    promises.push(this.useAudioStream(null));
                }
                if (videoMuted && track?.mhTrack?.getType() === MEDIA_TYPE.VIDEO) {
                    promises.push(this.useVideoStream(null));
                }
            }

            Promise.allSettled(promises).then(() =>
                APP.UI.notifyInitiallyMuted()
            );
        });

        room.on(MHConferenceEvents.DATA_CHANNEL_OPENED, () => {
            APP.store.dispatch(dataChannelOpened());
        });

        room.on(MHConferenceEvents.DATA_CHANNEL_CLOSED, details => {
            _handleBridgeDataChannelClosed('bridge-data-channel-closed', details);
        });

        if (_consumeDebugFlagOncePerSession(DEBUG_FLAG_FORCE_BRIDGE_CHANNEL_CLOSED)) {
            window.setTimeout(() => {
                _handleBridgeDataChannelClosed('debug-bridge-data-channel-closed', {
                    debugInjected: true
                }, { escalate: true });
            }, 1200);
        }

        if (_consumeDebugFlagOncePerSession(DEBUG_FLAG_FORCE_UDP_BLOCK)) {
            window.setTimeout(() => {
                _handleUdpBlockedScenario('debug-udp-blocked');
            }, 1200);
        }

        // call hangup
        addUIListener(UIEvents.HANGUP, () => {
            this.hangup(true);
        });

        // logout
        addUIListener(UIEvents.LOGOUT, () => {
            AuthHandler.logout(room).then(url => {
                if (url) {
                    UIUtil.redirect(url);
                } else {
                    this.hangup(true);
                }
            });
        });

        addUIListener(UIEvents.AUTH_CLICKED, () => {
            AuthHandler.authenticate(room);
        });

        addUIListener(UIEvents.VIDEO_DEVICE_CHANGED, cameraDeviceId => {
            const videoWasMuted = this.isLocalVideoMuted();

            sendAnalytics(createDeviceChangedEvent('video', 'input'));

            createLocalTracksF({
                devices: [ 'video' ],
                cameraDeviceId,
                micDeviceId: null
            })
                .then(([ stream ]) => {
                    // if we are in audio only mode or video was muted before
                    // changing device, then mute
                    if (this.isAudioOnly() || videoWasMuted) {
                        return stream.mute().then(() => stream);
                    }

                    return stream;
                })
                .then(stream => {
                    logger.log('Switching the local video device.');
                    if (stream) {
                        return this.useVideoStream(stream);
                    }
                })
                .then(() => {
                    logger.log('Switched local video device.');
                    this._updateVideoDeviceId();
                })
                .catch(error => {
                    logger.error(
                        `Switching the local video device failed: ${error}`
                    );

                    return APP.store.dispatch(notifyCameraError(error));
                });
        });

        addUIListener(UIEvents.TOGGLE_AUDIO_ONLY, audioOnly => {
            // FIXME On web video track is stored both in redux and in
            // 'localVideo' field, video is attempted to be unmuted twice when
            // turning off the audio only mode. This will crash the app with
            // 'unmute operation is already in progress'.
            // Because there's no logic in redux about creating new track in
            // case unmute when not track exists the things have to go through
            // muteVideo logic in such case.
            const tracks = APP.store.getState()['features/base/tracks'];
            const isTrackInRedux
                = Boolean(
                    tracks.find(
                        track => track.mhTrack
                            && track?.mhTrack?.getType() === 'video'));

            if (!isTrackInRedux) {
                this.muteVideo(audioOnly);
            }

            // Immediately update the UI by having remote videos and the large
            // video update themselves instead of waiting for some other event
            // to cause the update, usually PARTICIPANT_CONN_STATUS_CHANGED.
            // There is no guarantee another event will trigger the update
            // immediately and in all situations, for example because a remote
            // participant is having connection trouble so no status changes.
            APP.UI.updateAllVideos();
        });

        addUIListener(
            UIEvents.TOGGLE_SCREENSHARING,
            this.toggleScreenSharing.bind(this)
        );
    },

    /**
     * Cleanups local conference on suspend.
     */
    onSuspendDetected() {
        // After wake up, we will be in a state where conference is left
        // there will be dialog shown to user.
        // We do not want video/audio as we show an overlay and after it
        // user need to rejoin or close, while waking up we can detect
        // camera wakeup as a problem with device.
        // We also do not care about device change, which happens
        // on resume after suspending PC.
        if (this.deviceChangeListener) {
            MeetHourJS.mediaDevices.removeEventListener(
                MHMediaDevicesEvents.DEVICE_LIST_CHANGED,
                this.deviceChangeListener
            );
        }

        this.localVideo = null;
        this.localAudio = null;
    },

    /**
     * Callback invoked when the conference has been successfully joined.
     * Initializes the UI and various other features.
     *
     * @private
     * @returns {void}
     */
    _onConferenceJoined() {
        APP.UI.initConference();

        if (!config.disableShortcuts) {
            APP.keyboardshortcut.init();
        }

        // Initialize noise suppression if enabled in settings
        const state = APP.store.getState();

        if (getNoiseSuppressionEnabledOnStartup(state)) {
            APP.store.dispatch(setNoiseSuppressionEnabled(true));
        }

        APP.store.dispatch(conferenceJoined(room));
    },

    /**
     * Updates the list of current devices.
     *
     * @param {boolean} setDeviceListChangeHandler - Whether to add the deviceList change handlers.
     * @private
     * @returns {Promise}
     */
    _initDeviceList(setDeviceListChangeHandler = false) {
        const { mediaDevices } = MeetHourJS;

        if (
            mediaDevices.isDeviceListAvailable()
            && mediaDevices.isDeviceChangeAvailable()
        ) {
            if (setDeviceListChangeHandler) {
                // Called for every conference in the page; replacing the reference alone left the previous
                // listener on the mediaDevices singleton, so each device change ran once per past conference.
                if (this.deviceChangeListener) {
                    mediaDevices.removeEventListener(
                        MHMediaDevicesEvents.DEVICE_LIST_CHANGED,
                        this.deviceChangeListener
                    );
                }
                this.deviceChangeListener = devices =>
                    window.setTimeout(
                        () => this._onDeviceListChanged(devices),
                        0
                    );
                mediaDevices.addEventListener(
                    MHMediaDevicesEvents.DEVICE_LIST_CHANGED,
                    this.deviceChangeListener
                );
            }

            const { dispatch } = APP.store;

            return dispatch(getAvailableDevices()).then(devices => {
                // Ugly way to synchronize real device IDs with local
                // storage and settings menu. This is a workaround until
                // getConstraints() method will be implemented in browsers.
                this._updateAudioDeviceId();

                this._updateVideoDeviceId();

                APP.UI.onAvailableDevicesChanged(devices);
            });
        }

        return Promise.resolve();
    },

    /**
     * Updates the settings for the currently used video device, extracting
     * the device id from the used track.
     *
     * @private
     */
    _updateVideoDeviceId() {
        // `this.localVideo` is the DESKTOP track while a screenshare is running, so it is not the camera to
        // record the device of. The presenter track this used to fall back to in that case no longer exists -
        // the camera keeps its own track during a share - so read the camera from the store instead, otherwise
        // a camera picked mid-share is never remembered.
        const cameraTrack = this.localVideo?.videoType === 'desktop'
            ? getLocalMHVideoTrack(APP.store.getState())
            : this.localVideo;

        if (cameraTrack && cameraTrack.videoType === 'camera') {
            APP.store.dispatch(
                updateSettings({
                    cameraDeviceId: cameraTrack.getDeviceId()
                })
            );
        }
    },

    /**
     * Updates the settings for the currently used audio device, extracting
     * the device id from the used track.
     *
     * @private
     */
    _updateAudioDeviceId() {
        if (this.localAudio) {
            APP.store.dispatch(
                updateSettings({
                    micDeviceId: this.localAudio.getDeviceId()
                })
            );
        }
    },

    /**
     * Handles audio device changes.
     *
     * @param {string} micDeviceId - The new device id.
     * @returns {Promise}
     */
    async onAudioDeviceChanged(micDeviceId) {
        const audioWasMuted = this.isLocalAudioMuted();

        // Capture the user's noise-suppression intent before the track swap. The track that will be created
        // below has no effect attached (it is a fresh gUM stream); the only effect host we have on this
        // conference is the local audio MHLocalTrack that we are about to dispose. The intent itself - the
        // boolean redux flag and the persisted jitsiLocalStorage value - survives the swap because it lives
        // in the `features/noise-suppression` slice, but the EFFECT (the AudioWorklet, the RNNoise processor,
        // the audio graph wiring) lives on the track instance and is destroyed with it. We have to recreate
        // the effect against the new track after the swap resolves, otherwise the new mic emits raw audio.
        const noiseSuppressionWasEnabled = isNoiseSuppressionEnabled(APP.store.getState());

        // Tear down the existing effect on the outgoing track so its `setEffect(undefined)` chain completes
        // before we dispose the track. This mirrors the toggle-off path used by the settings dialog and
        // guarantees the AudioWorklet port is closed and the audio graph is unwired before we gUM a fresh
        // stream. We do NOT flip the persisted setting or the redux flag - keeping the intent intact is
        // what lets us re-apply below.
        if (noiseSuppressionWasEnabled) {
            await APP.store.dispatch(setNoiseSuppressionEnabled(false));
        }

        // When the 'default' mic needs to be selected, we need to pass the real device id to gUM instead of
        // 'default' in order to get the correct MediaStreamTrack from chrome because of the following bug.
        // https://bugs.chromium.org/p/chromium/issues/detail?id=997689.
        const isDefaultMicSelected = micDeviceId === 'default';
        const selectedDeviceId = isDefaultMicSelected
            ? getDefaultDeviceId(APP.store.getState(), 'audioInput')
            : micDeviceId;

        logger.info(`Switching audio input device to ${selectedDeviceId}`);
        sendAnalytics(createDeviceChangedEvent('audio', 'input'));
        createLocalTracksF({
            devices: [ 'audio' ],
            micDeviceId: selectedDeviceId
        })
        .then(([ stream ]) => {
            // if audio was muted before changing the device, mute
            // with the new device
            if (audioWasMuted) {
                return stream.mute()
                    .then(() => stream);
            }

            return stream;
        })
        .then(async stream => {
            await this._maybeApplyAudioMixerEffect(stream);

            return this.useAudioStream(stream);
        })
        .then(() => {
            const localAudio = getLocalMHAudioTrack(APP.store.getState());

            if (localAudio && isDefaultMicSelected) {
                // workaround for the default device to be shown as selected in the
                // settings even when the real device id was passed to gUM because of the
                // above mentioned chrome bug.
                localAudio._realDeviceId = localAudio.deviceId = 'default';
            }

            // Re-apply noise suppression on the freshly-swapped track. The effect host changed tracks: the
            // outgoing MHLocalTrack (the one we tore down with `setNoiseSuppressionEnabled(false)` above)
            // owned the AudioWorkletNode, and that node does not survive a track dispose. The new track has
            // no effect attached. If we leave the user's intent (enabled=true) satisfied by the persisted
            // flag without an actual effect, the user hears raw audio the moment they switch mics. The
            // redux flag is already correct (we never touched the persisted setting or the slice), so the
            // action just rebuilds the effect and dispatches setNoiseSuppressionEnabledState(true).
            if (noiseSuppressionWasEnabled) {
                APP.store.dispatch(setNoiseSuppressionEnabled(true));
            }
        })
        .catch(err => {
            logger.error(`Failed to switch to selected audio input device ${selectedDeviceId}, error=${err}`);
            APP.store.dispatch(notifyMicError(err));
        });
    },

    /**
     * Handles video device changes.
     *
     * @param {string} cameraDeviceId - The new device id.
     * @returns {void}
     */
    onVideoDeviceChanged(cameraDeviceId) {
        const videoWasMuted = this.isLocalVideoMuted();
        const localVideoTrack = getLocalMHVideoTrack(APP.store.getState());

        if (localVideoTrack?.getDeviceId() === cameraDeviceId) {
            return;
        }

        sendAnalytics(createDeviceChangedEvent('video', 'input'));

        createLocalTracksF({
            devices: [ 'video' ],
            cameraDeviceId
        })
        .then(([ stream ]) => {
            // if we are in audio only mode or video was muted before
            // changing device, then mute
            if (this.isAudioOnly() || videoWasMuted) {
                return stream.mute()
                    .then(() => stream);
            }

            return stream;
        })
        .then(stream =>

        //  logger.info(`Switching the local video device to ${cameraDeviceId}.`);

            this.useVideoStream(stream)
        )
        .catch(error => {
            logger.error(`Failed to switch to selected camera:${cameraDeviceId}, error:${error}`);

            return APP.store.dispatch(notifyCameraError(error));
        });
    },

    /**
     * Handles audio only changes.
     */
    onToggleAudioOnly() {
        // Immediately update the UI by having remote videos and the large video update themselves.
        const displayedUserId = APP.UI.getLargeVideoID();

        if (displayedUserId) {
            APP.UI.updateLargeVideo(displayedUserId, true);
        }
    },

    /**
     * Event listener for MHMediaDevicesEvents.DEVICE_LIST_CHANGED to
     * handle change of available media devices.
     *
     * @private
     * @param {MediaDeviceInfo[]} devices
     * @returns {Promise}
     */
    _onDeviceListChanged(devices) {
        const oldDevices
            = APP.store.getState()['features/base/devices'].availableDevices;

        APP.store.dispatch(updateDeviceList(devices));

        const newDevices
            = mediaDeviceHelper.getNewMediaDevicesAfterDeviceListChanged(
                devices,
                this.isSharingScreen,
                this.localVideo,
                this.localAudio
            );
        const promises = [];
        const audioWasMuted = this.isLocalAudioMuted();
        const videoWasMuted = this.isLocalVideoMuted();
        const requestedInput = {
            audio: Boolean(newDevices.audioinput),
            video: Boolean(newDevices.videoinput)
        };

        if (typeof newDevices.audiooutput !== 'undefined') {
            const { dispatch } = APP.store;
            const setAudioOutputPromise = setAudioOutputDeviceId(
                newDevices.audiooutput,
                dispatch
            ).catch(); // Just ignore any errors in catch block.

            promises.push(setAudioOutputPromise);
        }

        // Handles the use case when the default device is changed (we are always stopping the streams because it's
        // simpler):
        // If the default device is changed we need to first stop the local streams and then call GUM. Otherwise GUM
        // will return a stream using the old default device.
        if (requestedInput.audio && this.localAudio) {
            this.localAudio.stopStream();
        }

        if (requestedInput.video && this.localVideo) {
            this.localVideo.stopStream();
        }

        // Let's handle unknown/non-preferred devices
        const newAvailDevices
            = APP.store.getState()['features/base/devices'].availableDevices;
        let newAudioDevices = [];
        let oldAudioDevices = [];

        if (typeof newDevices.audiooutput === 'undefined') {
            newAudioDevices = newAvailDevices.audioOutput;
            oldAudioDevices = oldDevices.audioOutput;
        }

        if (!requestedInput.audio) {
            newAudioDevices = newAudioDevices.concat(
                newAvailDevices.audioInput
            );
            oldAudioDevices = oldAudioDevices.concat(oldDevices.audioInput);
        }

        // check for audio
        if (newAudioDevices.length > 0) {
            APP.store.dispatch(
                checkAndNotifyForNewDevice(newAudioDevices, oldAudioDevices)
            );
        }

        // check for video
        if (!requestedInput.video) {
            APP.store.dispatch(
                checkAndNotifyForNewDevice(
                    newAvailDevices.videoInput,
                    oldDevices.videoInput
                )
            );
        }

        // When the 'default' mic needs to be selected, we need to
        // pass the real device id to gUM instead of 'default' in order
        // to get the correct MediaStreamTrack from chrome because of the
        // following bug.
        // https://bugs.chromium.org/p/chromium/issues/detail?id=997689
        const hasDefaultMicChanged = newDevices.audioinput === 'default';

        // This is the case when the local video is muted and a preferred device is connected.
        if (requestedInput.video && this.isLocalVideoMuted()) {
            // We want to avoid creating a new video track in order to prevent turning on the camera.
            requestedInput.video = false;
            APP.store.dispatch(
                updateSettings({
                    // Update the current selected camera for the device selection dialog.
                    cameraDeviceId: newDevices.videoinput
                })
            );
            delete newDevices.videoinput;

            // Removing the current video track in order to force the unmute to select the preferred device.
            logger.debug(
                '_onDeviceListChanged: Removing the current video track.'
            );

            // this.useVideoStream(null);
        }

        promises.push(
            mediaDeviceHelper
                .createLocalTracksAfterDeviceListChanged(
                    createLocalTracksF,
                    newDevices.videoinput,
                    hasDefaultMicChanged
                        ? getDefaultDeviceId(APP.store.getState(), 'audioInput')
                        : newDevices.audioinput
                )
                .then(tracks => {
                    // If audio or video muted before, or we unplugged current
                    // device and selected new one, then mute new track.
                    const muteSyncPromises = tracks.map(track => {
                        if (
                            (track.isVideoTrack() && videoWasMuted)
                            || (track.isAudioTrack() && audioWasMuted)
                        ) {
                            return track.mute();
                        }

                        return Promise.resolve();
                    });

                    return Promise.all(muteSyncPromises).then(() =>
                        Promise.all(
                            Object.keys(requestedInput).map(mediaType => {
                                if (requestedInput[mediaType]) {
                                    const useStream
                                        = mediaType === 'audio'
                                            ? this.useAudioStream.bind(this)
                                            : this.useVideoStream.bind(this);

                                    // Use the new stream or null if we failed to obtain it.
                                    return useStream(
                                        tracks.find(
                                            track =>
                                                track.getType() === mediaType
                                        ) || null
                                    ).then(() => {
                                        if (
                                            this.localAudio
                                            && hasDefaultMicChanged
                                        ) {
                                            // workaround for the default device to be shown as selected in the
                                            // settings even when the real device id was passed to gUM because of the
                                            // above mentioned chrome bug.
                                            this.localAudio._realDeviceId
                                                = this.localAudio.deviceId
                                                = 'default';
                                        }
                                        mediaType === 'audio'
                                            ? this._updateAudioDeviceId()
                                            : this._updateVideoDeviceId();
                                    });
                                }

                                return Promise.resolve();
                            })
                        )
                    );
                })
                .then(() => {
                    // Log and sync known mute state.
                    if (audioWasMuted) {
                        sendAnalytics(
                            createTrackMutedEvent(
                                'audio',
                                'device list changed'
                            )
                        );
                        logger.log('Audio mute: device list changed');
                        muteLocalAudio(true);
                    }

                    if (!this.isSharingScreen && videoWasMuted) {
                        sendAnalytics(
                            createTrackMutedEvent(
                                'video',
                                'device list changed'
                            )
                        );
                        logger.log('Video mute: device list changed');
                        muteLocalVideo(true);
                    }
                })
        );

        return Promise.all(promises).then(() => {
            APP.UI.onAvailableDevicesChanged(devices);
        });
    },

    /**
     * Determines whether or not the audio button should be enabled.
     */
    updateAudioIconEnabled() {
        const audioMediaDevices
            = APP.store.getState()['features/base/devices'].availableDevices
                .audioInput;
        const audioDeviceCount = audioMediaDevices
            ? audioMediaDevices.length
            : 0;

        // The audio functionality is considered available if there are any
        // audio devices detected or if the local audio stream already exists.
        const available = audioDeviceCount > 0 || Boolean(this.localAudio);

        APP.store.dispatch(setAudioAvailable(available));
        APP.API.notifyAudioAvailabilityChanged(available);
    },

    /**
     * Determines whether or not the video button should be enabled.
     */
    updateVideoIconEnabled() {
        const videoMediaDevices
            = APP.store.getState()['features/base/devices'].availableDevices
                .videoInput;
        const videoDeviceCount = videoMediaDevices
            ? videoMediaDevices.length
            : 0;

        // The video functionality is considered available if there are any
        // video devices detected or if there is local video stream already
        // active which could be either screensharing stream or a video track
        // created before the permissions were rejected (through browser
        // config).
        const available = videoDeviceCount > 0 || Boolean(this.localVideo);

        APP.store.dispatch(setVideoAvailable(available));
        APP.API.notifyVideoAvailabilityChanged(available);
    },

    /**
     * Disconnect from the conference and optionally request user feedback.
     *
     * @param {boolean} [requestFeedback=false] - If user feedback should be
     * requested.
     */
    async hangup(requestFeedback = false) {
        _stopRuntimeWebSocketRecoveryProbe();
        this.autoReconnectSuppressed = true;
        this.isReconnecting = false;
        _clearAutoReconnectLifecycleState();

        if (LocalRecordingManager.isRecordingLocally()) {
            APP.store.dispatch(stopLocalVideoRecording());
            APP.store.dispatch(showWarningNotification({
                titleKey: 'localRecording.stop',
                descriptionKey: 'localRecording.wait'
            }, NOTIFICATION_TIMEOUT_TYPE.STICKY));

            // Wait for the file to be finalized before anything below can take the page away.
            await new Promise(resolve => window.setTimeout(resolve, 1000));
        }

        const { partId } = APP.store.getState()['features/genericiframe'];
        const participants = APP.store.getState()['features/base/participants'];
        const participantsD = participants.filter(i => i.local === true);
        const stateMent
            = APP.store.getState()['features/genericiframe'].visible;
        const { editing, partId: participantID }
            = APP.store.getState()['features/etherpad'];
        const { timeJoined, conferenceExpired } = APP.store.getState()['features/base/conference'];

        if (conferenceExpired) {
            setTimeout(() => {
                APP.store.dispatch(updateConferenceCallEnded(true));
            }, 4000);
        } else {
            APP.store.dispatch(updateConferenceCallEnded(true));
        }


        if (stateMent && participantsD[0]?.id === partId) {
            APP.store.dispatch(sendWhiteBoardInstance(false));
        }
        if (editing && participantID === participantsD[0]?.id) {
            APP.store.dispatch(toogleEtherPad(false));
        }
        try {
            const format1 = 'YYYY-MM-DD HH:mm:ss';
            const currentDateTime = moment().utc()
                .format(format1);
            const bowser = new BrowserDetection();
            const browserName = bowser.getName();
            let accessType = 'web';
            const participant = getLocalParticipant(APP.store.getState());

            // `timeJoined` is only set by USER_JOINED_TIME, which is dispatched from CONFERENCE_JOINED,
            // and it defaults to null. Hanging up before that - from prejoin, during connection, or after
            // a connection failure such as the websocket drops seen alongside these reports - left it
            // null, and `timeJoined.getHours()` then threw a TypeError. The throw was caught by the outer
            // handler at the end of this block, which logged the bare "Error in sending user attendance"
            // and skipped the request entirely, so a purely diagnostic log line was what stopped the
            // attendance record from ever being sent. Nothing below consumes these values.
            if (timeJoined) {
                const timeEnd = new Date();
                const difference = timeEnd.getHours() - timeJoined.getHours();
                const getMin
                    = timeEnd.getMinutes() > timeJoined.getMinutes()
                        ? timeEnd.getMinutes() - timeJoined.getMinutes()
                        : timeJoined.getMinutes() - timeEnd.getMinutes();

                logger.log('hours1233456', timeJoined, `${difference}:${getMin}`);
            }

            for (const i in bowserNameToJitsiName) {
                if (i !== browserName) {
                    accessType = 'app';
                }
            }

            let dataJson;

            try {

                const ClientIP = await fetchAxiosApi(
                    'https://api.ipify.org/?format=json'
                );

                // eslint-disable-next-line no-negated-condition
                if (typeof ClientIP !== 'undefined') {

                    const getData = await fetchAxiosApi(
                        `geolocation?api_key=${apiKEY}&ip_address=${ClientIP?.ip}`,
                        {
                            method: 'get',
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        }
                    );

                    // eslint-disable-next-line max-depth
                    if (getData) {
                        dataJson = await getData;
                    }
                }
            } catch (errors) {
                logger.error('Error in fetching geolocation bros', errors);
            }

            const roomName = this.roomName || getRoomName();

            const { occurenceId } = APP.store.getState()['features/base/mtApi'];
            const moement = moment().seconds(0)
                .milliseconds(0)
                .format('X');

            const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

            const userObject = {
                client_id: config.MH_CLIENT_ID,
                credentials: mesh256,
                ...isDomainWeborNative,
                meeting_id: roomName,
                name: participant?.name,
                occurrence_id: occurenceId,
                is_host: APP.store.getState()['features/base/jwt'].jwt ? 1 : 0,
                email_id: participant?.email,
                logout_datetime: currentDateTime,
                user_agent: browserName,
                access_type: accessType,
                ip: dataJson?.IPv4,
                geolocation: dataJson
            };

            const userJson = JSON.stringify(userObject);
            const sendUserData = await fetchAxiosApi(
                `${config.API_URL_PREFIX}${config.API_VERSION_PREFIX}meeting/save_meeting_attendance`,
                {
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: userJson
                }
            );

            if (sendUserData && sendUserData.status === 1082) {
                logger.log('User attendance sent successfully on hangup');
            }

        } catch (e) {
            logger.error('Error in sending user attendance', e);
        }

        APP.store.dispatch(disableReceiver());

        this._stopProxyConnection();

        // Recorded before leaveRoom() below, which has the conference detach its local tracks - and the TRACK_REMOVED
        // handler then drops them from the store without disposing them. A destroyLocalTracks() dispatched here reads
        // the store only after waiting for any getUserMedia in progress, by which time the leave had emptied it: it
        // disposed nothing, and the camera and microphone stayed on after leaving the meeting.
        this._rememberLocalTracksBeforeLeave();
        this._localTracksInitialized = false;
        this.localVideo = null;
        this.localAudio = null;

        // Discard pending replaceLocalTrack tasks. After hangup the conferences
        // and local MHTrack references these tasks hold are disposed; letting
        // them run anyway would mark redux state for a track that no longer
        // corresponds to anything in the (about-to-be-new) conference and
        // produce the "indicator unmuted but no one can hear me" symptom on
        // leave-and-rejoin. The conference join path re-attaches any local
        // tracks that ended up orphaned in redux during this window.
        _replaceLocalAudioTrackQueue.clear();
        _replaceLocalVideoTrackQueue.clear();

        // Remove unnecessary event listeners from firing callbacks.
        if (this.deviceChangeListener) {
            MeetHourJS.mediaDevices.removeEventListener(
                MHMediaDevicesEvents.DEVICE_LIST_CHANGED,
                this.deviceChangeListener
            );
        }

        APP.UI.removeAllListeners();

        let requestFeedbackPromise;

        if (requestFeedback) {
            requestFeedbackPromise = APP.store
                .dispatch(maybeOpenFeedbackDialog(room))

                // false because the thank you dialog shouldn't be displayed
                .catch(() => Promise.resolve(false));
        } else {
            requestFeedbackPromise = Promise.resolve(true);
        }

        // All promises are returning Promise.resolve to make Promise.all to
        // be resolved when both Promises are finished. Otherwise Promise.all
        // will reject on first rejected Promise and we can redirect the page
        // before all operations are done.

        const leavePromise = this.leaveRoom().catch(() => Promise.resolve());

        // leaveRoom() has already detached the tracks - MHConference.leave() does that before it waits on anything -
        // so this only stops capture. Not awaited, as before: nothing here should hold up leaving the page.
        this._destroyLocalTracksOfLeftRoom();

        Promise.allSettled([ requestFeedbackPromise, leavePromise ]).then(([ feedback ]) => {
            this._room = undefined;
            room = undefined;

            /**
             * Don't call {@code notifyReadyToClose} if the promotional page flag is set
             * and let the page take care of sending the message, since there will be
             * a redirect to the page anyway.
             */
            if (!interfaceConfig.SHOW_PROMOTIONAL_CLOSE_PAGE) {
                APP.API.notifyReadyToClose();
            }

            setTimeout(() => {
                APP.store.dispatch(maybeRedirectToWelcomePage(feedback.value ?? {}));
            }, 1500);
        });
    },


    /**
     * Leaves the room.
     *
     * @param {boolean} doDisconnect - Whether leaving the room should also terminate the connection.
     * @param {string} reason - reason for leaving the room.
     * @returns {Promise}
     */
    leaveRoom(doDisconnect = true, reason = '') {
        _stopRuntimeWebSocketRecoveryProbe();
        this.autoReconnectSuppressed = true;
        this.isReconnecting = false;
        _clearAutoReconnectLifecycleState();
        APP.store.dispatch(conferenceWillLeave(room));

        const maybeDisconnect = () => {
            if (doDisconnect) {
                return disconnect();
            }
        };

        if (room && room.isJoined()) {
            this._rememberLocalTracksBeforeLeave();

            return room.leave(reason).then(() => maybeDisconnect())
                .catch(e => {
                    logger.error(e);

                    return maybeDisconnect();
                });
        }

        return maybeDisconnect();
    },

    /**
     * Leaves the room and calls MHConnection.disconnect.
     *
     * @returns {Promise}
     */
    leaveRoomAndDisconnect() {
        _stopRuntimeWebSocketRecoveryProbe();
        this.autoReconnectSuppressed = true;
        this.isReconnecting = false;
        _clearAutoReconnectLifecycleState();
        APP.store.dispatch(conferenceWillLeave(room));

        if (room && room.isJoined()) {
            return room.leave().then(disconnect, disconnect);
        }

        return disconnect();
    },

    /** ........
     * Changes the email for the local user
     *
     * @param email {string} the new email
     */
    changeLocalEmail(email = '') {
        const localParticipant = getLocalParticipant(APP.store.getState()) || {};

        const formattedEmail = String(email).trim();

        if (formattedEmail === localParticipant.email) {
            return;
        }

        const localId = localParticipant.id;

        APP.store.dispatch(
            participantUpdated({
                // XXX Only the local participant is allowed to update without
                // stating the MHConference instance (i.e. participant property
                // `conference` for a remote participant) because the local
                // participant is uniquely identified by the very fact that there is
                // only one local participant.

                id: localId,
                local: true,
                email: formattedEmail
            })
        );

        APP.store.dispatch(
            updateSettings({
                email: formattedEmail
            })
        );
        APP.API.notifyEmailChanged(localId, {
            email: formattedEmail
        });
        sendData(commands.EMAIL, formattedEmail);
    },

    changeLocalOrganization(organization = '') {
        const localParticipant = getLocalParticipant(APP.store.getState()) || {};

        const formattedOrganization = String(organization).trim();

        if (formattedOrganization === localParticipant.organization) {
            return;
        }

        APP.API.notifyOrganisationChnaged(localParticipant.id, {
            organization: formattedOrganization
        });

        APP.store.dispatch(
            participantOrganization(localParticipant.id, formattedOrganization)
        );

        APP.store.getState()['features/base/conference'].conference.sendCommand(ORGANIZATION_COMMAND, { value: formattedOrganization });
        sendData(ORGANIZATION_COMMAND, formattedOrganization);
    },

    changeLocalDesignation(designation = '') {
        const localParticipant = getLocalParticipant(APP.store.getState()) || {};

        const formattedDesignation = String(designation).trim();

        if (formattedDesignation === localParticipant.designation) {
            return;
        }

        APP.API.notifyDesignationChnages(localParticipant.id, {
            designation: formattedDesignation
        });

        APP.store.dispatch(
            participantDesignation(localParticipant.id, formattedDesignation)
        );

        APP.store.getState()['features/base/conference'].conference.sendCommand(DESIGNATION_COMMAND, { value: formattedDesignation });
        sendData(DESIGNATION_COMMAND, formattedDesignation);
    },

    /** ........
     * Changes the avatar url for the local user
     *
     * @param url {string} the new url
     */
    changeLocalAvatarUrl(url = '') {
        const { avatarURL, id } = getLocalParticipant(APP.store.getState()) || {};

        const formattedUrl = String(url).trim();

        if (formattedUrl === avatarURL) {
            return;
        }

        APP.store.dispatch(
            participantUpdated({
                // XXX Only the local participant is allowed to update without
                // stating the MHConference instance (i.e. participant property
                // `conference` for a remote participant) because the local
                // participant is uniquely identified by the very fact that there is
                // only one local participant.

                id,
                local: true,
                avatarURL: formattedUrl
            })
        );

        APP.store.dispatch(
            updateSettings({
                avatarURL: formattedUrl
            })
        );
        sendData(commands.AVATAR_URL, url);
    },

    /**
     * Sends a message via the data channel.
     *
     * @param {string} to - The id of the endpoint that should receive the
     * message. If "" - the message will be sent to all participants.
     * @param {Object} payload - The payload of the message.
     * @throws NetworkError or InvalidStateError or Error if the operation
     * fails.
     */
    sendEndpointMessage(to, payload) {
        room.sendEndpointMessage(to, payload);
    },

    /**
     * Adds new listener.
     *
     * @param {string} eventName - The name of the event.
     * @param {Function} listener - The listener.
     */
    addListener(eventName, listener) {
        eventEmitter.addListener(eventName, listener);
        eventEmitter.setMaxListeners(2000);
    },

    /**
     * Removes listener.
     *
     * @param {string} eventName - The name of the event that triggers the
     * listener.
     * @param {Function} listener - The listener.
     */
    removeListener(eventName, listener) {
        eventEmitter.removeListener(eventName, listener);
    },

    /** ........
     * Changes the display name for the local user
     *
     * @param nickname {string} the new display name
     */
    changeLocalDisplayName(nickname = '') {
        const formattedNickname = getNormalizedDisplayName(nickname);
        const { id, name } = getLocalParticipant(APP.store.getState()) || {};

        if (formattedNickname === name) {
            return;
        }

        APP.store.dispatch(
            participantUpdated({
                // XXX Only the local participant is allowed to update without
                // stating the MHConference instance (i.e. participant property
                // `conference` for a remote participant) because the local
                // participant is uniquely identified by the very fact that there is
                // only one local participant.

                id,
                local: true,
                name: formattedNickname
            })
        );

        APP.store.dispatch(
            updateSettings({
                displayName: formattedNickname
            })
        );

        APP.API.notifyDisplayNameChanged(id, {
            displayName: formattedNickname,
            formattedDisplayName: appendSuffix(formattedNickname || interfaceConfig.DEFAULT_LOCAL_DISPLAY_NAME)
        });

        APP.store.getState()['features/base/conference'].conference.setDisplayName(formattedNickname);
    },

    /**
     * Callback invoked by the external api create or update a direct connection
     * from the local client to an external client.
     *
     * @param {Object} event - The object containing information that should be
     * passed to the {@code ProxyConnectionService}.
     * @returns {void}
     */
    onProxyConnectionEvent(event) {
        if (!this._proxyConnection) {
            this._proxyConnection = new MeetHourJS.ProxyConnectionService({
                /**
                 * Pass the {@code MHConnection} instance which will be used
                 * to fetch TURN credentials.
                 */
                mhConnection: APP.connection,

                /**
                 * The proxy connection feature is currently tailored towards
                 * taking a proxied video stream and showing it as a local
                 * desktop screen.
                 */
                convertVideoToDesktop: true,

                /**
                 * Callback invoked when the connection has been closed
                 * automatically. Triggers cleanup of screensharing if active.
                 *
                 * @returns {void}
                 */
                onConnectionClosed: () => {
                    if (this._untoggleScreenSharing) {
                        this._untoggleScreenSharing();
                    }
                },

                /**
                 * Callback invoked to pass messages from the local client back
                 * out to the external client.
                 *
                 * @param {string} peerJid - The jid of the intended recipient
                 * of the message.
                 * @param {Object} data - The message that should be sent. For
                 * screensharing this is an iq.
                 * @returns {void}
                 */
                onSendMessage: (peerJid, data) =>
                    APP.API.sendProxyConnectionEvent({
                        data,
                        to: peerJid
                    }),

                /**
                 * Callback invoked when the remote peer of the proxy connection
                 * has provided a video stream, intended to be used as a local
                 * desktop stream.
                 *
                 * @param {MHLocalTrack} remoteProxyStream - The media
                 * stream to use as a local desktop stream.
                 * @returns {void}
                 */
                onRemoteStream: desktopStream => {
                    if (desktopStream.videoType !== 'desktop') {
                        logger.warn('Received a non-desktop stream to proxy.');
                        desktopStream.dispose();

                        return;
                    }

                    this.toggleScreenSharing(undefined, { desktopStream });
                }
            });
        }

        this._proxyConnection.processMessage(event);
    },

    /**
     * Sets the video muted status.
     *
     * @param {boolean} muted - New muted status.
     */
    setVideoMuteStatus() {
        APP.UI.setVideoMuted(this.getMyUserId());

        // APP.API.notifyVideoMutedStatusChanged(muted);
    },

    /**
     * Sets the audio muted status.
     *
     * @param {boolean} muted - New muted status.
     */
    setAudioMuteStatus(muted) {
        APP.UI.setAudioMuted(this.getMyUserId(), muted);
        APP.API.notifyAudioMutedStatusChanged(muted);
    },

    /**
     * Dispatches the passed in feedback for submission. The submitted score
     * should be a number inclusively between 1 through 5, or -1 for no score.
     *
     * @param {number} score - A number between 1 and 5 (inclusive) or -1 for no
     * score.
     * @param {string} message - An optional message to attach to the feedback
     * in addition to the score.
     * @returns {void}
     */
    submitFeedback(score = -1, message = '') {
        if (score === -1 || (score >= 1 && score <= 5)) {
            APP.store.dispatch(submitFeedback(score, message, room));
        }
    },

    /**
     * Terminates any proxy screensharing connection that is active.
     *
     * @private
     * @returns {void}
     */
    _stopProxyConnection() {
        if (this._proxyConnection) {
            this._proxyConnection.stop();
        }

        this._proxyConnection = null;
    }
};
